import React, { useRef, useEffect, useState, useCallback, useContext, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../../config";
import { purewebContext } from "../../../context/PurewebContext";
import useArcwareStream from "./arcware/useArcwareStream";
import useTabKeepAlive from "./interlucent/useTabKeepAlive";
import LoginPage from "./interlucent/LoginPage";
import LoginPageAdmin from "./interlucent/LoginPageAdmin";
import MenuPanel from "./interlucent/MenuPanel";
import Upload3DContainer from "./interlucent/Upload3DContainer";
import UploadWidget, { limitImageResolution } from "../../UploadWidget/UploadWidget";
import DownloadBar from "./interlucent/DownloadBar";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import ReportIssueModal from "./interlucent/ReportIssueModal";
import "./interlucent/interlucent.css";

// Arcware Cloud WebSDK credentials are managed from the admin panel (the single
// renderGoals settings doc) instead of .env. Dev and prod share one database, so the
// doc holds both pairs; this deployment picks its pair via REACT_APP_ENVIRONMENT.
const IS_PRODUCTION = process.env.REACT_APP_ENVIRONMENT === "production";

// Clarity can't capture the streamed Unreal Engine video/canvas (a known,
// unresolved Clarity platform limitation — microsoft/clarity#647), so every
// recording goes black once streaming starts. These forward the UE actions we
// already parse as Clarity Smart Events / tags, so *what happened* during the
// black segment is still visible (filterable in the Clarity dashboard) even
// though *what it looked like* isn't.
const clarityEvent = (name) => {
  try { window.clarity?.("event", name); } catch (_) {}
};
const claritySet = (key, value) => {
  try { window.clarity?.("set", key, value); } catch (_) {}
};

// How long the frozen last frame stays on screen after a disconnect — long
// enough for Clarity to record it and for the user to see what they lost.
const DISCONNECT_FRAME_MS = 4000;

// Direct login (?action=login): UE announces RenderNow_Loaded before its login
// state is ready to receive credentials — LoginEmail/LoginPassword sent in that
// same instant land in the void, and UE fires a PlayFab login with BOTH fields
// empty (it replies with the raw "Invalid input parameters … field is required"
// report). Manual logins never hit this because typing takes seconds. So the
// auto-submit waits a grace period first, and if the empty-credentials error
// still comes back it resends after another grace period, a few times at most.
const AUTO_LOGIN_GRACE_MS = 3000;
const AUTO_LOGIN_MAX_ATTEMPTS = 3;

// ── RenderNow_Loaded is a one-shot broadcast, so treat it as an accelerator ────
// UE emits "RenderNow_Loaded" exactly once, unprompted, with no acknowledgement.
// If it is emitted before our data channel is open, the SDK's streamer-side sender
// discards it and it is gone — and since canSkipVideo is the ONLY thing that renders
// the Continue button, the user sits on the loading screen until the 90s refresh
// modal. That window is wide open for users far from the streaming region: ICE, the
// DTLS handshake and SCTP setup all cost round trips, so at ~300ms RTT our channel
// opens seconds later than a US user's while the UE instance runs on its own clock.
// (Arcware confirmed the stream itself is healthy in these sessions — video arrives,
// it's our side that never unblocks.)
//
// So once the transport is genuinely up in BOTH directions — video decoding AND the
// data channel open — we give the message this long to show up, then unblock anyway.
// UE is demonstrably running by then, so proceeding is correct rather than a guess;
// the ue_loaded_fallback_used event tells us how often we lean on it.
const UE_LOADED_FALLBACK_MS = 8000;
// How long we wait for UE to answer a Google trigger with `open_url` before treating
// it as never having arrived. Generous: it also covers UE still booting.
const GOOGLE_AUTH_TIMEOUT_MS = 30000;
// Once the consent URL is open the user is off in another tab: they have to pick an
// account, consent, and then UE runs a SECOND trip (mode=check then mode=code) before
// it can call PlayFab. The normal 40s login budget (App.js LOGIN_TIMEOUT_SEC) is far
// too short for that and fires "Login timed out" while sign-in is still working, so
// that timer is stopped and this longer one takes over.
const GOOGLE_CONSENT_TIMEOUT_MS = 240000;
// How long the loading screen stays up after a Google SDK_Login_Success. UE reports
// success while its own login screen is still on the stream and needs 5+ seconds to
// move on (client testing, Nov 2026), and dropping our overlay at that moment exposes
// it. UE can end this early with RenderNow_Editor_Ready; this is the cap until then.
const GOOGLE_POST_LOGIN_HOLD_MS = 8000;

// Google and Microsoft sign-in reach UE by a different route than email/password — a
// single pre-authorised token instead of a credential pair — but everything around
// that (validate with the backend, hold until UE is ready, then send) is identical.
// These are the only per-provider differences; note the backend's parameter names are
// NOT symmetric between the two, hence the explicit body builders.
const SOCIAL_LOGIN = {
  google: {
    label: "Google",
    // Google is a TRIGGER, not a credential: the browser performs no OAuth at all.
    // UE owns the whole exchange (it builds the consent URL, polls /auth/status and
    // redeems the code with PlayFab) because the account chooser must render in the
    // user's real browser — UE itself runs on a remote Arcware GPU, so anything it
    // opens locally is invisible to the user. No validatePath and no token: nothing
    // exists to pre-validate, and the email only comes back on
    // `SDK_Login_Success,<email>`.
    trigger: true,
    ueMessage: () => "Google_Login_Trigger",
  },
  microsoft: {
    label: "Microsoft",
    validatePath: "validateMicrosoftToken",
    validateBody: (token) => ({ accessToken: token }),
    playfabBody: (email, token) => ({ email, msToken: token }),
    ueMessage: (token) => `Login_Token_Microsoft,${token}`,
  },
};

// Shown in the placeholder tab between the user's click and UE returning a consent
// URL. Without it the tab sits blank and reads as a bug.
const GOOGLE_TAB_HOLDING_PAGE = `<!doctype html>
<html><head><meta charset="utf-8"><title>Connecting to Google…</title></head>
<body style="margin:0;display:grid;place-items:center;height:100vh;font:16px/1.5 system-ui,sans-serif;background:#f7f7f8;color:#3c4043">
  <div style="text-align:center">
    <p style="font-size:18px;font-weight:600;margin:0 0 8px">Connecting to Google…</p>
    <p style="margin:0;color:#70757a">This tab will redirect automatically.</p>
  </div>
</body></html>`;

// ── Scene export file name ───────────────────────────────────────────────────
// The S3 key already carries the right extension (.obj / .gltf / .glb / .zip),
// so the download name is taken from the URL path rather than hardcoded. Query
// strings (pre-signed URL params) are stripped first; anything unusable falls
// back to a generic zip name, which is what the multi-file formats produce.
const exportFileNameFromUrl = (url) => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const name = decodeURIComponent(path.split("/").pop() || "");
    if (name && /\.[a-z0-9]{2,5}$/i.test(name)) return name;
  } catch (err) {
    console.warn("Could not derive export file name from URL:", url, err);
  }
  return "SceneExport.zip";
};

const VeoLinkPopup = ({ url, onClose, title = "Video Link" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!navigator.clipboard) {
      const el = document.createElement("textarea");
      el.value = url;
      el.style.cssText = "position:fixed;top:0;left:0";
      document.body.appendChild(el);
      el.focus();
      el.select();
      try { document.execCommand("copy"); setCopied(true); } catch (_) {}
      document.body.removeChild(el);
      return;
    }
    navigator.clipboard.writeText(url).then(() => setCopied(true)).catch(() => {});
  };

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <div className="download-image-popup--container" style={{ gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <LinkIcon sx={{ color: "#1976d2", fontSize: 22 }} />
        <span style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap" }}>{title}</span>
      </div>
      <div
        className="copy-container"
        onClick={handleCopy}
        title={copied ? "Copied!" : "Click to copy"}
        style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
      >
        <ContentCopyIcon className="copy-container-icon" sx={{ padding: 0.3, flexShrink: 0 }} />
        <div className="copy-container--text" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {copied ? "Copied!" : url}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
        <button
          onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          style={{
            display: "flex", alignItems: "center", gap: 4,
            padding: "6px 12px", borderRadius: 6, border: "none",
            backgroundColor: "#1976d2", color: "white",
            fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 16 }} />
          Open
        </button>
        <button
          onClick={onClose}
          style={{
            display: "flex", alignItems: "center",
            padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc",
            backgroundColor: "white", color: "#333",
            fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

// Maps captured Arcware stream signals + the derived status to a human-readable
// disconnect cause for the Report-an-Issue payload / console diagnosis.
const getDisconnectCause = (events, status) => {
  const has = (type) => events.some((e) => e.type === type);
  const lastOf = (type) => [...events].reverse().find((e) => e.type === type);
  const lastDisconnect = lastOf("webRtcDisconnected");
  if (has("afkTimedOut") || /inactiv/i.test(lastDisconnect?.detail?.reason ?? ""))
    return "AFK timeout — the user was inactive and the countdown expired (not a network/server failure)";
  const lastError = lastOf("error");
  if (lastError?.detail?.reason)
    return `Server error: ${lastError.detail.reason}`;
  if (has("error"))
    return "Server / session error reported by the signalling server";
  if (lastDisconnect?.detail?.reason)
    return `Stream disconnected: ${lastDisconnect.detail.reason}`;
  if (has("websocketClose")) {
    const { code, reason } = lastOf("websocketClose")?.detail ?? {};
    const suffix = [code && `code ${code}`, reason].filter(Boolean).join(", ");
    return `Signalling WebSocket closed — the stream connection dropped${suffix ? ` (${suffix})` : ""}`;
  }
  // A resume attempt skips provisioning and attaches straight to signalling ("Initiating
  // connection to stream signaling.." with no "Connecting to the cloud infrastructure.."
  // before it). If the instance it targets is already gone there's nothing behind the
  // signalling step, so the connect just never completes.
  const lastIndexOf = (type) => events.map((e) => e.type).lastIndexOf(type);
  const lastAttemptIdx = lastIndexOf("connectAttempt");
  if (lastAttemptIdx > -1 && lastAttemptIdx > lastIndexOf("videoInitialized")) {
    if (events[lastAttemptIdx].detail?.resume)
      return "Connect never completed — this attempt asked Arcware to RESUME a previous session, so no new instance was provisioned";
  }
  if (has("queue"))
    return "Stuck in the streaming queue — no GPU instance became available";
  if (status === "failed" || status === "error")
    return "Stream reported a failed state (no more specific event captured)";
  if (status === "ended")
    return "Stream reported a terminal state (ended)";
  return "Unknown — no diagnostic events were captured before the disconnect";
};

/** Session is over or unrecoverable from the app’s perspective — show interrupt UI. */
const TERMINAL_INTERRUPT_STATUSES = new Set(["error", "failed", "ended"]);
/** Pending states that indicate an active reconnect attempt (drives the 90s modal). */
const PENDING_RECONNECT_STATUSES = new Set(["connecting", "negotiating"]);
/** States that show the "connecting…" pending overlay (includes queued). */
const PENDING_OVERLAY_STATUSES = new Set(["connecting", "negotiating", "queued"]);
/** Idle states — show the "click play" overlay. */
const IDLE_STATUSES = new Set(["idle", "connected"]);
/** Paused/ready state — show the resume overlay. */
const READY_STATUSES = new Set(["ready"]);

// ── Credentials fetch policy ─────────────────────────────────────────────────
// The Share ID request gates the whole page, so it gets a deadline and retries.
// Worst case before we give up: 3 × 10s + 0.8s + 1.6s ≈ 32.4s (STREAM_START_TIMEOUT_MS
// below must stay above this, or the watchdog fires while a retry is still in flight).
const CREDENTIALS_TIMEOUT_MS = 10000;
const CREDENTIALS_MAX_ATTEMPTS = 3;
const CREDENTIALS_RETRY_BASE_MS = 800;

// Hard deadline on OUR side of startup: credentials → SDK constructed → video host
// mounted. Nothing past that point is covered — once Arcware is contacted the connection
// has its own timeouts and its own status messages, and this must not second-guess them.
// Sized to clear the credentials retry budget (~32.4s) plus the video-host mount (~5s of
// animation frames) with room to spare.
const STARTUP_READY_TIMEOUT_MS = 45000;

const buildIssueDiagnostics = (reason, getStatus, disconnectEventsRef, statusHistoryRef) => {
  const copy = REFRESH_MODAL_COPY[reason] ?? REFRESH_MODAL_COPY.stream;
  const events = disconnectEventsRef.current;
  const statusHistory = statusHistoryRef.current;
  const status = getStatus?.() ?? "unknown";
  const cause = getDisconnectCause(events, status);

  const eventLines = events.slice(-20).map(({ time, type, detail }) => {
    const t = time.slice(0, 19).replace("T", " ");
    const d =
      detail && Object.keys(detail).length > 0
        ? "  " + JSON.stringify(detail)
        : "";
    return `  ${t}  ${type}${d}`;
  }).join("\n") || "  (none captured)";

  return {
    issueTitle: copy.title,
    cause,
    pageUrl: window.location.href,
    timeOfIssue: new Date().toLocaleString(),
    pixelStreamStatus: status,
    browser: navigator.userAgent,
    statusHistory: statusHistory.join("\n") || "  (none recorded)",
    recentEvents: eventLines,
  };
};

const REFRESH_MODAL_COPY = {
  loading: {
    title: "Something went wrong",
    description: "We're sorry! This doesn't happen often, but something went wrong with the connection. Please refresh the page or let us know what happened.",
    primary: { action: "refresh", label: "Refresh page" },
    secondary: null,
  },
  login: {
    title: "Login timed out",
    description: "Sign-in didn't finish in time. Refresh the page to start over.",
    primary: { action: "refresh", label: "Refresh page" },
    secondary: null,
  },
  stream: {
    title: "Connection lost",
    description: "There was a disconnection. Your progress is saved. Please refresh the page or let us know what happened so that we can help!",
    primary: { action: "refresh", label: "Refresh page" },
    secondary: null,
  },
  afk: {
    title: "Session paused due to inactivity",
    description: "You were away for more than 20 minutes, so we saved your progress and paused the session. Just refresh the page to jump back in.",
    primary: { action: "refresh", label: "Refresh page" },
    secondary: null,
  },
};

const btnPrimaryStyle = {
  padding: "12px 28px",
  borderRadius: 8,
  border: "none",
  backgroundColor: "#111",
  color: "white",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const btnSecondaryStyle = {
  padding: "12px 28px",
  borderRadius: 8,
  border: "1.5px solid #ccc",
  backgroundColor: "white",
  color: "#333",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const RefreshRequiredModal = ({ reason, onReconnect, onReportIssue }) => {
  const copy = REFRESH_MODAL_COPY[reason] ?? REFRESH_MODAL_COPY.stream;

  const runAction = (action) => {
    if (action === "refresh") window.location.reload();
    else onReconnect();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        // Above the login overlay (10006): the stream can die while the login page is
        // open, and this modal must be what the user sees — not a dead login form.
        zIndex: 10008,
        backgroundColor: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: 12,
          padding: "36px 40px",
          maxWidth: 440,
          width: "90%",
          textAlign: "center",
          fontFamily: "sans-serif",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}
      >
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 12px", color: "#111" }}>
          {copy.title}
        </h2>
        <p style={{ fontSize: 15, color: "#555", margin: "0 0 24px", lineHeight: 1.5 }}>
          {copy.description}
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          {copy.secondary && (
            <button
              type="button"
              onClick={() => runAction(copy.secondary.action)}
              style={btnSecondaryStyle}
            >
              {copy.secondary.label}
            </button>
          )}
          <button
            type="button"
            onClick={() => runAction(copy.primary.action)}
            style={btnPrimaryStyle}
          >
            {copy.primary.label}
          </button>
        </div>
        <p className="il-modal-report">
          <button
            type="button"
            className="il-modal-report-link"
            onClick={onReportIssue}
          >
            Report an issue
          </button>
        </p>
      </div>
    </div>
  );
};

const MONDAY_FORM_URL =
  "https://forms.monday.com/forms/embed/dbed330cd9e897fc506c94500e243594?r=use1";

// Remember that the Monday intake form was already submitted, so a page refresh — which
// people do when the "Create New Account" step looks stuck — doesn't drop them back onto
// a blank intake form and make them fill it out again (someone re-submitted it 3× in one
// morning). sessionStorage on purpose: it survives the refresh (the actual complaint) but
// resets for a genuinely new browser session, so it can never go stale across days. It's
// cleared once the user finishes logging in (SDK_Login_Success).
const INTAKE_SUBMITTED_KEY = "rn_signup_intake_submitted";
const isIntakeSubmitted = () => {
  try { return sessionStorage.getItem(INTAKE_SUBMITTED_KEY) === "1"; } catch (_) { return false; }
};
const markIntakeSubmitted = () => {
  try { sessionStorage.setItem(INTAKE_SUBMITTED_KEY, "1"); } catch (_) {}
};
const clearIntakeSubmitted = () => {
  try { sessionStorage.removeItem(INTAKE_SUBMITTED_KEY); } catch (_) {}
};

const SignupIntakeModal = ({ onSubmit }) => {
  const mountedAtRef = useRef(Date.now());
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handleMessage = (e) => {
      if (typeof e.origin !== "string" || !e.origin.includes("monday.com")) return;
      if (Date.now() - mountedAtRef.current < 3000) return;

      let data = e.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch (_) { return; }
      }
      if (!data || typeof data !== "object") return;

      if (data.source === "workforms" && data.type === "submit_success") {
        // Persist BEFORE the confirmation screen so a refresh at any point after the
        // form is actually submitted (even before "Continue to Create My Account" is
        // clicked) skips the intake form instead of re-showing it.
        markIntakeSubmitted();
        setSubmitted(true);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10010,
      backgroundColor: "rgba(0,0,0,0.78)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px 16px",
    }}>
      <div style={{
        backgroundColor: "white", borderRadius: 14,
        width: "100%", maxWidth: 700, height: "90vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
        overflow: "hidden",
      }}>
        {submitted ? (
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: "40px 48px", fontFamily: "sans-serif", textAlign: "center",
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h2 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 12px", color: "#111" }}>
              Form submitted — you're almost there!
            </h2>
            <p style={{ fontSize: 15, color: "#444", margin: "0 0 24px", lineHeight: 1.7, maxWidth: 480 }}>
              We've sent you a welcome email with everything you need to get started —
              including how our team will personally build a custom 3D venue template for your
              next event or pitch, completely free.
            </p>
            <div style={{
              backgroundColor: "#f6f8fa", borderRadius: 10, padding: "18px 24px",
              marginBottom: 28, textAlign: "left", width: "100%", maxWidth: 460,
              border: "1px solid #e2e8f0",
            }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: "#555", margin: "0 0 12px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                What's in your email
              </p>
              {[
                "How to send us your venue floor plan so we can pre-build your template",
                "Step-by-step instructions to create your RenderNow account",
                "Links to tutorials, videos, and live chat support",
                "A link to book a VIP onboarding call with our team",
              ].map((item) => (
                <div key={item} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                  <span style={{ color: "#0AAC41", fontWeight: 700, fontSize: 16, lineHeight: 1.4, flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: 14, color: "#333", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
            <button
              onClick={onSubmit}
              style={{
                padding: "13px 36px", borderRadius: 8, border: "none",
                backgroundColor: "#0AAC41", color: "white",
                fontSize: 15, fontWeight: 700, cursor: "pointer",
              }}
            >
              Continue to Create My Account →
            </button>
          </div>
        ) : (
          <iframe
            src={MONDAY_FORM_URL}
            title="Start Your Free Trial"
            style={{ flex: 1, border: "none", width: "100%" }}
          />
        )}
      </div>
    </div>
  );
};

const InterlucentEmulator = () => {
  const location = useLocation();
  const history = useHistory();

  // Stop the browser from auto-discarding this tab while it's backgrounded (the
  // "left the tab for a few minutes and it silently refreshed" complaint). Emits
  // an inaudible keepalive tone while hidden so the tab stays protected across the
  // gaps where the stream itself isn't producing audio (loading, login, post-AFK).
  useTabKeepAlive();

  const signupMode = useMemo(
    () => new URLSearchParams(location.search).get("action") === "signup",
    [location.search]
  );
  // Viewer (share-link) mode: arriving via /render?share=<jwt>. The token carries the
  // account owner's email; once validated we auto-start the stream and tell UE to enter
  // a read-only viewer mode for that owner (see ViewerMode message below).
  const shareToken = useMemo(
    () => new URLSearchParams(location.search).get("share"),
    [location.search]
  );
  // Admin-login mode: arriving via /render?action=adminlogin. The stream menu opens this
  // in a new tab so the admin login popup never disrupts the original stream/connection;
  // the popup is shown automatically on this fresh tab (see effect below).
  const adminLoginMode = useMemo(
    () => new URLSearchParams(location.search).get("action") === "adminlogin",
    [location.search]
  );
  // Direct-login mode: arriving via /render?action=login (the eventrender.com "Login"
  // button). The login popup opens immediately over the loading screen; credentials
  // are validated against the backend (PlayFab) right away, then held until UE sends
  // RenderNow_Loaded, at which point login fires automatically — so the user never
  // has to log in twice or wait for the stream before finding out their password is
  // wrong. Unknown emails are steered into the signup intake flow instead.
  const directLoginMode = useMemo(
    () => new URLSearchParams(location.search).get("action") === "login",
    [location.search]
  );
  // TEST ONLY: /render?...&debugFreezeFrame=1 auto-triggers a genuine disconnect 5s
  // after login so the freeze-frame overlay + Connection Lost flow can be seen in a
  // real (prod) environment. Never fires without the flag, so real users are unaffected.
  const debugFreezeFrameMode = useMemo(
    () => new URLSearchParams(location.search).get("debugFreezeFrame") === "1",
    [location.search]
  );
  // Keeps a downscaled frame updating BEHIND the live video so Clarity's replay
  // (where <video> is blank) shows a time-lapse of the whole session. Invisible to
  // the user — the opaque stream covers it. On for everyone; the frame skip + 10s
  // cadence keep the Clarity payload small. Escape hatch: /render?...&claritySnapshots=0.
  const claritySnapshotsMode = useMemo(
    () => new URLSearchParams(location.search).get("claritySnapshots") !== "0",
    [location.search]
  );

  const loadedMessageReceivedRef = useRef(0);
  // When the latest RenderNow_Loaded arrived — anchors the auto-login grace window
  // (AUTO_LOGIN_GRACE_MS): a submit right after load waits out the remainder of the
  // window, a submit long after load sends immediately.
  const loadedAtRef = useRef(null);
  // Bridges the stream adapter's callbacks (registered once) to the latest
  // handleMessage / maybeSendViewerMode without re-initialising the SDK.
  const handleMessageRef = useRef(null);
  const dataChannelOpenRef = useRef(null);
  // Timers driven by the derived stream status (replace Interlucent's status
  // MutationObserver). Stored in refs so they persist across status re-renders.
  const queueTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const terminalTimeoutRef = useRef(null);

  const {
    loginVisible,
    setLoginVisible,
    upload3DContainerVisible,
    setUpload3DContainerVisible,
    imgbbContainerVisible,
    setImgbbContainerVisible,
    imageTo3DContainerVisible,
    setImageTo3DContainerVisible,
    downloadVisible,
    setDownloadVisible,
    preparingDownload,
    setPreparingDownload,
    renderStarted,
    setRenderStarted,
    imagefileone,
    setImagefileone,
    videofileone,
    setVideofileone,
    renderDownloadUrl,
    setRenderDownloadUrl,
    exportDownloadUrl,
    setExportDownloadUrl,
    setConnectionSuccessLoadingApplication,
    isAdmin,
    setIsAdmin,
    setPlayfabData,
    setUserLoggedIn,
    setHasLoggedInOnce,
    hasLoggedInOnce,
    startTimer,
    stopTimer,
    stopLoadingTimer,
    startLoadingTimer,
    loadingTimeLeft,
    refreshRequiredModalVisible,
    setRefreshRequiredModalVisible,
    refreshModalReason,
    setRefreshModalReason,
    canSkipVideo,
    setCanSkipVideo,
    playfabData,
  } = useContext(purewebContext);

  // Stable adapter callbacks — they read the latest handlers from refs so the SDK's
  // one-time message / data-channel listeners never need re-binding.
  const onMessageStable = useCallback((m) => handleMessageRef.current?.(m), []);
  const onDataChannelOpenStable = useCallback(() => dataChannelOpenRef.current?.(), []);

  /** Wall-clock start of the current connection attempt, for stage timings. */
  const connectionStartRef = useRef(null);

  // ── Connection stage timings ────────────────────────────────────────────────
  // Which stage a session reaches, and how long each took, is the only way to tell
  // "the stream never connected" apart from "the stream connected and our flow
  // failed to notice" — the two look identical to the user (a loading screen that
  // never ends) and we had no way to distinguish them in the field. Clarity already
  // segments by country, so these tags give us the near-region vs far-region
  // comparison directly.
  const stageSeenRef = useRef({});
  const reportStage = useCallback((stage, detail) => {
    const startedAt = connectionStartRef.current;
    const sinceStart = startedAt ? Date.now() - startedAt : null;
    if (stage === "rtt") {
      claritySet("stream_rtt_ms", String(detail?.rttMs ?? ""));
      return;
    }
    if (stage === "send_queued") {
      clarityEvent("stream_send_queued");
      return;
    }
    if (stage === "transport_dead") {
      claritySet("stream_transport_dead_cause", String(detail?.cause ?? ""));
      clarityEvent("stream_transport_dead");
      return;
    }
    // First occurrence only: reconnects would otherwise overwrite the timings of
    // the initial connect, which is the one that decides whether the user got in.
    if (stageSeenRef.current[stage]) return;
    stageSeenRef.current[stage] = true;
    if (sinceStart != null) claritySet(`t_${stage}_ms`, String(sinceStart));
    if (stage === "data_channel_open") {
      // The discriminator for the whole investigation: when the channel opens AFTER
      // the first frame, any RenderNow_Loaded UE sent in between was discarded.
      claritySet("channel_open_before_video", String(!detail?.videoUp));
    }
  }, []);

  // Arcware credentials are managed from the admin panel (renderGoals settings doc),
  // not .env. Fetch them on mount and pick the dev/prod pair for this deployment.
  const [arcwareConfig, setArcwareConfig] = useState({ shareId: "", projectId: "" });
  // Why the credentials never arrived, once we've stopped trying. Null while a fetch is
  // still in flight AND after a successful load — `arcwareConfig.shareId` distinguishes
  // those two. This replaces a bare boolean so a failure can name WHICH fault it was.
  //
  // Without a Share ID the SDK is never built, `ready` never turns true, and no connect
  // is ever attempted — so every fault here ends as an indefinite wait on "Starting your
  // session…" unless it reports itself. Three separate ones used to end there silently:
  //
  //   1. A STALLED socket. axios defaults to NO timeout (0 = infinite), so a connection
  //      that hangs rather than fails never settles — neither .then nor .catch ever runs
  //      and nothing downstream can tell. Fixed by CREDENTIALS_TIMEOUT_MS.
  //   2. A FALSY body. The old handler bailed on `!data` without recording a failure,
  //      leaving the page in neither the loaded nor the failed state. A 2xx carrying
  //      nothing usable is now a failure like any other.
  //   3. ANY transient blip, because one mount-time attempt has no second chance and the
  //      only recovery was a full page reload. This is why this call broke while every
  //      other caller of the same API looked healthy: elsewhere a blip is either
  //      user-retryable or non-fatal; here it is terminal for the entire page.
  const [credentialsError, setCredentialsError] = useState(null);
  // Surfaced on the idle overlay ("attempt 2 of 3") so a slow start looks like progress
  // rather than a hang, and recorded in the diagnostics block.
  const [credentialsAttempt, setCredentialsAttempt] = useState(1);
  const [credentialsRetryToken, setCredentialsRetryToken] = useState(0);
  // Set by the startup deadline below when our bring-up never finishes but nothing threw
  // — the case where every individual step is still "in progress" and nobody gives up.
  const [startupTimedOut, setStartupTimedOut] = useState(null);
  const retryCredentials = useCallback(() => {
    setCredentialsError(null);
    setStartupTimedOut(null);
    setCredentialsAttempt(1);
    setCredentialsRetryToken((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    const controller = new AbortController();

    const attempt = async (n) => {
      if (cancelled) return;
      setCredentialsAttempt(n);
      try {
        const { data } = await axios.get(`${API_BASE_URL}/getRenderGoals`, {
          timeout: CREDENTIALS_TIMEOUT_MS,
          signal: controller.signal,
        });
        if (cancelled) return;
        // Fault 2. A 200 with an empty or non-object body is a failure, not a no-op —
        // this is the branch that used to `return` silently and strand the page.
        if (!data || typeof data !== "object") {
          console.error("[arcware] credentials response was empty or not an object:", data);
          setCredentialsError("credentials response was empty or not an object");
          return;
        }
        const shareId = IS_PRODUCTION ? data.arcwareShareId : data.arcwareShareIdDev;
        const projectId = IS_PRODUCTION
          ? data.arcwareProjectId
          : data.arcwareProjectIdDev;
        // A configuration fault, not a transport one: retrying will not conjure a Share
        // ID into the settings doc, so fail immediately and name the environment (the
        // prod/dev split is decided at BUILD time by REACT_APP_ENVIRONMENT).
        if (!shareId) {
          const env = IS_PRODUCTION ? "production" : "development";
          console.error(`[arcware] no Share ID configured for ${env}`);
          setCredentialsError(`no Arcware Share ID configured for ${env}`);
          return;
        }
        setArcwareConfig({ shareId, projectId: projectId || "" });
        setCredentialsError(null);
      } catch (err) {
        // An aborted request is our own unmount, not a fault.
        if (cancelled || axios.isCancel?.(err) || err?.code === "ERR_CANCELED") return;
        const detail =
          err?.code === "ECONNABORTED"
            ? `timed out after ${CREDENTIALS_TIMEOUT_MS}ms`
            : err?.response
              ? `HTTP ${err.response.status}`
              : err?.message || "network error";
        // Fault 3. Retry transport failures with backoff so one bad second isn't fatal,
        // and so we don't hammer an API that's already struggling.
        if (n < CREDENTIALS_MAX_ATTEMPTS) {
          console.warn(
            `[arcware] credentials attempt ${n}/${CREDENTIALS_MAX_ATTEMPTS} failed ` +
              `(${detail}) — retrying`
          );
          retryTimer = setTimeout(
            () => attempt(n + 1),
            CREDENTIALS_RETRY_BASE_MS * 2 ** (n - 1)
          );
          return;
        }
        console.error(`[arcware] credentials failed after ${n} attempts: ${detail}`);
        setCredentialsError(`credentials request failed after ${n} attempts (${detail})`);
      }
    };

    attempt(1);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      controller.abort();
    };
  }, [credentialsRetryToken]);

  // ── Arcware stream adapter ──────────────────────────────────
  const {
    containerRef,
    status: psStatus,
    ready,
    channelOpen,
    initError,
    play,
    cancel,
    stop,
    sendMessage,
    disconnectEventsRef,
    statusHistoryRef,
    getStatus,
    getConnectionHealth,
    afkWarning,
    afkTimedOut,
    dismissAfkWarning,
    disconnectSnapshot,
    liveSnapshot,
    connectionMessages,
    debugForceDisconnect,
    bootTraceRef,
  } = useArcwareStream({
    shareId: arcwareConfig.shareId,
    projectId: arcwareConfig.projectId,
    onMessage: onMessageStable,
    onDataChannelOpen: onDataChannelOpenStable,
    onStage: reportStage,
    enableLiveSnapshots: claritySnapshotsMode,
  });

  // Local state
  const [showLoadingTutorial, setShowLoadingTutorial] = useState(false);
  const [noWorkerAvailable, setNoWorkerAvailable] = useState(false);
  // On a fresh signup visit the intake form shows; on a refresh AFTER it was submitted
  // we skip straight to account creation. Seeding signupFormSubmitted from the same flag
  // is what advances the flow past the (now hidden) form once UE is ready — without it a
  // refresh would hide the intake form but leave the user stuck on the loading video.
  const [signupIntakeVisible, setSignupIntakeVisible] = useState(
    () => signupMode && !isIntakeSubmitted()
  );
  const [signupFormSubmitted, setSignupFormSubmitted] = useState(
    () => signupMode && isIntakeSubmitted()
  );
  // Once the user has created an account (or finished login), never reopen the login
  // overlay in "New Account" mode — signupMode stays true for the whole URL session.
  const [signupNewAccountDone, setSignupNewAccountDone] = useState(false);
  // Direct-login (?action=login): backend-validated credentials held until UE sends
  // RenderNow_Loaded, then submitted automatically. `autoLoginPending` puts the loading
  // screen in its "signing you in" state from validation until SDK_Login_Success.
  // `autoLoginSentRef` makes the auto-submit single-shot — every resend is a fresh
  // PlayFab auth attempt (see LoginPage's retry notes), so a re-emitted
  // RenderNow_Loaded must not fire the credentials a second time. The only resends
  // allowed are the empty-credentials retries (see handleMessage), counted in
  // `autoLoginAttemptsRef` — those are lockout-safe because an empty attempt isn't
  // a failed password try and the stored credentials are backend-validated.
  const [autoLoginPending, setAutoLoginPending] = useState(false);
  const pendingLoginRef = useRef(null);
  const autoLoginSentRef = useRef(false);
  const autoLoginAttemptsRef = useRef(0);
  const autoLoginTimersRef = useRef([]);
  useEffect(() => {
    const timers = autoLoginTimersRef.current;
    return () => timers.forEach(clearTimeout);
  }, []);
  // Armed while the loading screen is held after a Google login (see
  // GOOGLE_POST_LOGIN_HOLD_MS).
  const googleRevealTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(googleRevealTimerRef.current), []);
  // Linking a Google login to an existing password account. UE reports the match
  // (`Google_Account_Found`), the user enters their password once, and UE links after
  // that login. The email locks the form; the refs say a link is in flight, whether the
  // password login already succeeded, and hold the backstop timer; the notice is the
  // short message shown if linking fails.
  const [googleLinkEmail, setGoogleLinkEmail] = useState("");
  const [googleLinkNotice, setGoogleLinkNotice] = useState("");
  const googleLinkActiveRef = useRef(false);
  const googleLinkLoginDoneRef = useRef(false);
  const googleLinkTimerRef = useRef(null);
  const googleLinkNoticeTimerRef = useRef(null);
  useEffect(
    () => () => {
      clearTimeout(googleLinkTimerRef.current);
      clearTimeout(googleLinkNoticeTimerRef.current);
    },
    []
  );
  const hasLoggedInOnceRef = useRef(false);
  useEffect(() => {
    hasLoggedInOnceRef.current = hasLoggedInOnce;
  }, [hasLoggedInOnce]);

  // Reset ALL render-session state on every mount so /render always starts from scratch.
  //
  // This state lives in App.js (the SPA root), so it survives navigating away from /render
  // and back via the browser back button — App.js never unmounts. But every emulator mount
  // forces a brand-new UE instance (useArcwareStream clears the persisted Arcware session id
  // on mount, so the stream can't silently resume the previous session — it re-emits
  // RenderNow_Loaded, SDK_Login_Success, etc. from scratch). If we leave the old values in
  // place, the fresh session inherits stale UI: e.g. `hasLoggedInOnce` shows LoadingScreen's
  // dead-end "Reconnecting…" panel, and `canSkipVideo` shows "ready to launch" before the new
  // instance has loaded anything. Clear everything here so the mounted UI matches the fresh
  // stream; each flag is re-set when UE re-emits its message (RenderNow_Loaded → canSkipVideo,
  // SDK_Login_Success → hasLoggedInOnce/userLoggedIn, …). Account data (isAdmin, playfabData,
  // tutorials) is intentionally NOT reset — it isn't per-session render state.
  useEffect(() => {
    // login / launch flags
    setHasLoggedInOnce?.(false);
    setUserLoggedIn?.(false);
    setCanSkipVideo?.(false);
    setConnectionSuccessLoadingApplication?.(false);
    // render output + download bar
    setRenderStarted?.(false);
    setDownloadVisible?.(false);
    setPreparingDownload?.(false);
    setImagefileone?.("");
    setVideofileone?.("");
    setRenderDownloadUrl?.("");
    setExportDownloadUrl?.("");
    // modals / overlays
    setLoginVisible?.(false);
    setImgbbContainerVisible?.(false);
    setUpload3DContainerVisible?.(false);
    setImageTo3DContainerVisible?.(false);
    setRefreshRequiredModalVisible?.(false);
    setRefreshModalReason?.(null);
    // local RenderNow_Loaded guard, so the re-emitted message is processed again
    loadedMessageReceivedRef.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Viewer-mode lifecycle: validating the token, validated (auto-start), or invalid.
  const [viewerMode, setViewerMode] = useState(false);
  const [viewerValidating, setViewerValidating] = useState(Boolean(shareToken));
  const [viewerError, setViewerError] = useState(false);
  // Share-link popup (admin minting a link) — url for success, message for failure.
  const [shareLinkUrl, setShareLinkUrl] = useState("");
  const [shareError, setShareError] = useState("");
  // Refs so the data-channel-open / RenderNow_Loaded handlers always read the current
  // viewer-mode state without re-binding listeners or churning useCallback deps.
  const viewerModeRef = useRef(false);
  const shareTokenRef = useRef(shareToken);
  useEffect(() => { viewerModeRef.current = viewerMode; }, [viewerMode]);
  useEffect(() => { shareTokenRef.current = shareToken; }, [shareToken]);
  const [veoLinkUrl, setVeoLinkUrl] = useState("");
  const [loginVisibleAdmin, setLoginVisibleAdmin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [adminSectionOpen, setAdminSectionOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const [reportIssueDiagnostics, setReportIssueDiagnostics] = useState(null);
  /** True once the stream has reached streaming at least once this mount (for reconnect copy). */
  const [hasEverStreamed, setHasEverStreamed] = useState(false);

  // Newest Arcware signaling status ("Connecting to the cloud infrastructure..",
  // …) — shown on the pending overlay, and mirrored into a Clarity tag so
  // sessions can be filtered by how far the connection got even though the
  // recording is black once streaming starts.
  const latestConnectionMessage =
    connectionMessages[connectionMessages.length - 1]?.text ?? "";
  useEffect(() => {
    if (latestConnectionMessage) {
      claritySet("arcware_connection_status", latestConnectionMessage);
    }
  }, [latestConnectionMessage]);
  // Shown verbatim. We used to skip past "… successfully." completions to surface the
  // newest IN-PROGRESS line instead, on the theory that a completion describes a step
  // that has already finished. That was us deciding which of Arcware's messages the user
  // should see — once the connection is under way the signalling server's own words are
  // the status, unedited and unreordered, so the newest one is simply the newest one.
  const pendingStatusMessage = latestConnectionMessage;

  // Everything our side of the connection must finish before Arcware is contacted at
  // all: credentials fetched → SDK constructed → its video host mounted. `ready` is the
  // hook's single signal for all three, so it is exactly the right gate for the Play
  // button — pressing play before it is true could never have connected.
  //
  // Every way that bring-up can end badly resolves to ONE of these, and each names the
  // step it died on: a screenshot of this state is enough to place the fault without
  // asking the user to open a console. `detail` is the raw cause, kept verbatim.
  const startupFailure = credentialsError
    ? {
        headline: "We couldn’t load your session settings.",
        detail: credentialsError,
        recoverable: true,
      }
    : initError
      ? {
          headline: "We couldn’t start the streaming player.",
          detail: initError,
          recoverable: false,
        }
      : startupTimedOut
        ? {
            headline: "Your session didn’t finish starting.",
            detail: startupTimedOut,
            // Only the credentials leg can be retried in place; past that the SDK has to
            // be rebuilt, which only a reload does.
            recoverable: !arcwareConfig.shareId,
          }
        : null;
  const startupBlocked = Boolean(startupFailure);
  const canStartSession = ready && !startupBlocked;

  // Auto-open the admin login popup when arriving via ?action=adminlogin (the stream
  // menu opens this in a new tab so logging in never disrupts the original stream).
  useEffect(() => {
    if (adminLoginMode) setLoginVisibleAdmin(true);
  }, [adminLoginMode]);

  // Auto-open the user login popup when arriving via ?action=login, so the user can
  // enter credentials straight away. Skipped when a share token is present — viewer
  // mode never shows the login overlay.
  useEffect(() => {
    if (directLoginMode && !shareToken) setLoginVisible(true);
  }, [directLoginMode, shareToken, setLoginVisible]);

  // Something has asked for the stream; the connect itself waits for `ready`, because
  // the SDK can't be built until the Arcware credentials finish loading.
  const [streamRequested, setStreamRequested] = useState(false);
  const requestStream = useCallback(() => {
    console.log(`[arcware:boot +${Math.round(performance.now())}ms] stream-requested`);
    setStreamRequested(true);
  }, []);

  // Single-shot: the direct-login popup can rewrite the URL to ?action=signup ("Create
  // New Account"), and a second play() would tear down and reconnect the already-
  // connecting stream.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!streamRequested || !ready) return;
    if (autoStartedRef.current) {
      // Not an error: something asked for the stream while it was already running.
      // Returning here is what stops that turning into a reconnect.
      console.log("[arcware] stream already started — ignoring request (no reconnect)");
      return;
    }
    console.log(`[arcware:boot +${Math.round(performance.now())}ms] starting-stream`);
    autoStartedRef.current = true;
    connectionStartRef.current = Date.now();
    setShowLoadingTutorial(true);
    try { play(); } catch (_) {}
  }, [streamRequested, ready, play]);

  // One copy-pasteable block covering the whole start chain, for the "stuck on Starting
  // your session…" reports. Printed as a single pre-formatted string rather than an
  // object on purpose — console objects have to be expanded field by field before they
  // can be copied, and the point of this is to be pasted into a bug report whole.
  const buildStartupReport = useCallback(
    (reason) => {
      const pad = (k) => `${k}:`.padEnd(24);
      const container = containerRef.current;
      const video = container?.querySelector?.("video");
      const lines = [
        "",
        "═══════ RenderNow startup diagnostics ═══════",
        `${pad("reason")}${reason}`,
        `${pad("time")}${new Date().toISOString()}`,
        `${pad("url")}${window.location.href}`,
        `${pad("page age")}${Math.round(performance.now() / 1000)}s`,
        `${pad("environment")}${IS_PRODUCTION ? "production" : "development"}`,
        `${pad("api base")}${API_BASE_URL}`,
        "── credentials ──",
        `${pad("fetch state")}${
          credentialsError
            ? `FAILED — ${credentialsError}`
            : arcwareConfig.shareId
              ? "loaded"
              : `still pending (attempt ${credentialsAttempt} of ${CREDENTIALS_MAX_ATTEMPTS})`
        }`,
        `${pad("shareId")}${arcwareConfig.shareId || "(none)"}`,
        `${pad("projectId")}${arcwareConfig.projectId || "(none)"}`,
        "── sdk ──",
        `${pad("ready")}${ready}`,
        `${pad("initError")}${initError ?? "(none)"}`,
        `${pad("container mounted")}${Boolean(container)}`,
        `${pad("container children")}${container?.childElementCount ?? "n/a"}`,
        `${pad("video element")}${video ? `${video.videoWidth}x${video.videoHeight} readyState=${video.readyState}` : "(none)"}`,
        "── start request ──",
        `${pad("streamRequested")}${streamRequested}`,
        `${pad("play() called")}${autoStartedRef.current}`,
        `${pad("stream status")}${psStatus}`,
        `${pad("mode")}${
          [
            directLoginMode && "action=login",
            signupMode && "action=signup",
            adminLoginMode && "action=adminlogin",
            shareToken && "share-link",
          ].filter(Boolean).join(", ") || "normal"
        }`,
        `${pad("login pending")}${autoLoginPending}`,
        `${pad("tab hidden now")}${document.hidden}`,
        "── boot trace ──",
        ...(bootTraceRef.current.length
          ? bootTraceRef.current.map(
              ({ at, step, detail }) =>
                `  +${String(at).padStart(6)}ms  ${step}${detail ? `  ${JSON.stringify(detail)}` : ""}`
            )
          : ["  (empty — the stream hook never got past waiting for credentials)"]),
        "── server messages ──",
        ...(connectionMessages.length
          ? connectionMessages.map((m) => `  ${m.id}. ${m.text}`)
          : ["  (none received)"]),
        "── stream events ──",
        ...(disconnectEventsRef.current.length
          ? disconnectEventsRef.current.slice(-15).map(
              ({ time, type, detail }) =>
                `  ${time.slice(11, 23)}  ${type}${detail ? `  ${JSON.stringify(detail)}` : ""}`
            )
          : ["  (none)"]),
        "── status history ──",
        ...(statusHistoryRef.current.length
          ? statusHistoryRef.current.map((s) => `  ${s}`)
          : ["  (no transitions)"]),
        "═════════ end — copy everything above ═════════",
        "",
      ];
      return lines.join("\n");
    },
    [
      arcwareConfig.shareId,
      arcwareConfig.projectId,
      credentialsError,
      credentialsAttempt,
      ready,
      initError,
      streamRequested,
      psStatus,
      directLoginMode,
      signupMode,
      adminLoginMode,
      shareToken,
      autoLoginPending,
      connectionMessages,
      bootTraceRef,
      containerRef,
      disconnectEventsRef,
      statusHistoryRef,
    ]
  );

  // Available at any moment from the console, so a stuck screen can be captured even
  // when no timeout has fired yet.
  useEffect(() => {
    window.rnDiag = () => {
      console.log(buildStartupReport("manual (window.rnDiag)"));
      return "Diagnostics printed above — copy from ═══ to ═══ and send them over.";
    };
    return () => { delete window.rnDiag; };
  }, [buildStartupReport]);

  // A requested stream that never even starts CONNECTING is a fault, not a slow server,
  // and nothing else catches it: the loading/login timeouts all assume a connection is
  // in flight, and the SDK can't report a failure on a connect it never attempted. So
  // the user sat on "Starting your session…" indefinitely while one of three things had
  // silently gone wrong upstream — the credentials request never resolved, the SDK
  // failed to initialise, or its video host never mounted.
  //
  // `ready` is the single precondition for the connect, so waiting on it covers all
  // three (and anything else we haven't seen yet). The console line names which.
  //
  // Must stay above the credentials retry budget (~32.4s, see CREDENTIALS_* above),
  // or this fires while a perfectly healthy retry is still in flight.
  const STREAM_START_TIMEOUT_MS = 45000;
  useEffect(() => {
    if (!streamRequested || ready) return undefined;
    const fail = (detail) => {
      console.error(`[arcware] stream never started — ${detail}`);
      console.log(buildStartupReport(`stream never started — ${detail}`));
      claritySet("arcware_start_stall", detail);
      clarityEvent("arcware_stream_never_started");
      setRefreshModalReason((r) => r ?? "loading");
      setRefreshRequiredModalVisible(true);
      setShowLoadingTutorial(false);
    };
    // A known init failure needs no grace period — it is never going to recover.
    // credentialsError is only set once retries are exhausted or the fault is one
    // retrying can't fix, so reaching here means we really have given up.
    if (credentialsError) return void fail(credentialsError);
    if (initError) return void fail(initError);
    const timer = setTimeout(
      () => fail(
        arcwareConfig.shareId
          ? "SDK never became ready"
          // Retries would have set credentialsError, so still-pending here means the
          // request neither resolved nor rejected inside the whole budget.
          : `credentials still pending on attempt ${credentialsAttempt} of ${CREDENTIALS_MAX_ATTEMPTS}`
      ),
      STREAM_START_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [
    streamRequested,
    ready,
    initError,
    credentialsError,
    credentialsAttempt,
    arcwareConfig.shareId,
    buildStartupReport,
    setRefreshModalReason,
    setRefreshRequiredModalVisible,
  ]);

  // The watchdog above only arms once a stream has been REQUESTED — which the signup,
  // login and viewer flows all do, but a plain /render visit does NOT: it sits on the
  // idle overlay waiting for the Play button to appear. So if `ready` never arrived
  // there, nothing at all was watching, and the user waited on "Starting your session..."
  // with no deadline of any kind. That is the gap this closes.
  //
  // It stops where Arcware begins. Once a stream is requested the watchdog above owns the
  // case, and once we've connected the connection's own timeouts and Arcware's own status
  // messages do — this never speaks for either.
  const buildStartupReportRef = useRef(buildStartupReport);
  useEffect(() => { buildStartupReportRef.current = buildStartupReport; }, [buildStartupReport]);
  // Read at fire time, not captured: keeping it out of the deps is what stops an
  // unrelated re-render from restarting the timer, and a timer that keeps resetting is a
  // timer that never fires.
  const hasCredentialsRef = useRef(false);
  useEffect(() => {
    hasCredentialsRef.current = Boolean(arcwareConfig.shareId);
  }, [arcwareConfig.shareId]);

  useEffect(() => {
    // A named failure already tells the user where it broke — a deadline adds nothing.
    if (ready || credentialsError || initError || streamRequested) return undefined;
    const timer = setTimeout(() => {
      // Which step we died on is the whole value of this message, so say it plainly.
      const stage = hasCredentialsRef.current
        ? "the streaming player didn’t finish starting up"
        : "your session settings didn’t finish loading";
      console.error(`[arcware] startup timed out — ${stage}`);
      console.log(buildStartupReportRef.current?.(`startup timed out — ${stage}`));
      claritySet("arcware_start_stall", `timeout — ${stage}`);
      clarityEvent("arcware_startup_timeout");
      setStartupTimedOut(stage);
    }, STARTUP_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [ready, credentialsError, initError, streamRequested]);

  // ?action=signup starts the stream on arrival: the intake form is a genuine task that
  // fills the wait, so the connection may as well run underneath it.
  //
  // ?action=login deliberately does NOT. Starting on arrival meant the connection ran —
  // and aged — behind the opaque login overlay while the user read the form and typed,
  // so they met it already tens of seconds old (a progress bar long since pinned at its
  // cap, or an AFK countdown on a session they had never seen). The submit acts as the
  // play button instead, so the connection starts in front of them. It also means a
  // mistyped password no longer provisions a GPU instance.
  //
  // Admin-login (?action=adminlogin) never starts one: it only needs the PlayFab stats
  // that the login POST fetches, so a stream would be pure waste.
  useEffect(() => {
    if (signupMode) requestStream();
  }, [signupMode, requestStream]);

  // Validate a share token before doing anything else. On success we enter viewer mode;
  // on failure we surface the invalid-link screen instead of the stream/login.
  useEffect(() => {
    if (!shareToken) return;
    let cancelled = false;
    setViewerValidating(true);
    axios
      .post(`${API_BASE_URL}/validateShareToken`, { token: shareToken })
      .then(() => {
        if (cancelled) return;
        setViewerError(false);
        setViewerMode(true);
      })
      .catch(() => {
        if (cancelled) return;
        setViewerMode(false);
        setViewerError(true);
      })
      .finally(() => { if (!cancelled) setViewerValidating(false); });
    return () => { cancelled = true; };
  }, [shareToken]);

  // Auto-start the stream once the share token validates — mirrors the signup auto-start.
  useEffect(() => {
    if (!viewerMode || !ready) return;
    connectionStartRef.current = Date.now();
    setShowLoadingTutorial(true);
    try { play(); } catch (_) {}
  }, [viewerMode, ready, play]);

  // The 1Hz elapsed-time ticker that used to live here is gone with the copy it fed.
  // Its only consumer was the invented "Servers are spinning up…" ladder on the pending
  // overlay; with Arcware's own messages there instead, nothing reads the value and the
  // interval was re-rendering this component every second for nothing.
  // connectionStartRef survives — the Clarity connect-duration tag still reads it.

  // Login form state
  const [error, setError] = useState("");
  const [errorAdmin, setErrorAdmin] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginLoadingAdmin, setLoginLoadingAdmin] = useState(false);
  const [success, setSuccess] = useState("");
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [newAccountLoading, setNewAccountLoading] = useState(false);

  // Tell UE to enter viewer mode for the share token's owner. Sending the raw JWT (not
  // just the email) lets UE re-verify the signature with the shared secret and extract
  // the email itself. Fired on data-channel-open and again on RenderNow_Loaded as a
  // safety net in case the channel opened before the token finished validating.
  const maybeSendViewerMode = useCallback(() => {
    if (viewerModeRef.current && shareTokenRef.current) {
      sendMessage(`ViewerMode,${shareTokenRef.current}`);
    }
  }, [sendMessage]);

  // Admin mints a viewer share link: ask the backend to sign a token for the logged-in
  // email, then surface a copyable /render?share=<token> URL.
  const handleShare = useCallback(async () => {
    setShareError("");
    const email = localStorage.getItem("userEmail");
    if (!email) {
      setShareError("Please log in before creating a share link.");
      return;
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/generateShareToken`, { email });
      setShareLinkUrl(`${window.location.origin}/render?share=${res.data.token}`);
    } catch (e) {
      setShareError("Could not create a share link. Please try again.");
    }
  }, []);

  // Log out from the stream menu WITHOUT tearing down the live stream. The
  // Arcware/UE instance stays connected — we only reset the logged-in user and
  // re-open the login overlay over the still-running stream — so switching
  // accounts is instant instead of the full reconnect a page refresh would cost.
  // UE is asked to return to its pre-login state via `LogoutUser`; even if UE just
  // re-authenticates on the next LoginPassword, the switch still works because
  // every login is a fresh PlayFab auth (see LoginPage.sendLoginMessages).
  const handleLogout = useCallback(() => {
    clarityEvent("user_logout");
    // Reset UE's logged-in user but KEEP the stream/data channel open.
    try { sendMessage("LogoutUser"); } catch (_) {}

    // Drop every stored identity key so nothing resurfaces the previous user
    // (userEmail drives the render flow; the rest mirror the app-wide logout set).
    ["userEmail", "password", "token", "email", "fullname", "lastlogin"].forEach((k) => {
      try { localStorage.removeItem(k); } catch (_) {}
    });

    // Reset the app's session/user state (mirrors the mount reset) but deliberately
    // leave the stream alone: canSkipVideo / loadedMessageReceivedRef stay set, so
    // the re-login fires immediately and no loading screen reappears.
    pendingLoginRef.current = null;
    autoLoginSentRef.current = false;
    autoLoginAttemptsRef.current = 0;
    hasLoggedInOnceRef.current = false;
    clearTimeout(googleRevealTimerRef.current);
    googleRevealTimerRef.current = null;
    clearTimeout(googleLinkTimerRef.current);
    googleLinkTimerRef.current = null;
    googleLinkActiveRef.current = false;
    googleLinkLoginDoneRef.current = false;
    setGoogleLinkEmail("");
    setGoogleLinkNotice("");
    setAutoLoginPending(false);
    setUserLoggedIn(false);
    setHasLoggedInOnce(false);
    setIsAdmin(false);
    setPlayfabData([]);
    setError("");
    setSuccess("");
    // Clear the previous user's render output and any open modals.
    setRenderStarted(false);
    setDownloadVisible(false);
    setPreparingDownload(false);
    setImagefileone("");
    setVideofileone("");
    setRenderDownloadUrl("");
    setExportDownloadUrl("");
    setImgbbContainerVisible(false);
    setUpload3DContainerVisible(false);
    setImageTo3DContainerVisible(false);
    setVeoLinkUrl("");
    setRefreshRequiredModalVisible(false);
    setRefreshModalReason(null);
    // Never reopen in "New Account" mode after a logout, then show the login form.
    setSignupNewAccountDone(true);
    setLoginVisible(true);
  }, [
    sendMessage,
    setAutoLoginPending,
    setUserLoggedIn,
    setHasLoggedInOnce,
    setIsAdmin,
    setPlayfabData,
    setRenderStarted,
    setDownloadVisible,
    setPreparingDownload,
    setImagefileone,
    setVideofileone,
    setRenderDownloadUrl,
    setExportDownloadUrl,
    setImgbbContainerVisible,
    setUpload3DContainerVisible,
    setImageTo3DContainerVisible,
    setRefreshRequiredModalVisible,
    setRefreshModalReason,
    setLoginVisible,
  ]);

  const handleSkip = useCallback(() => {
    setShowLoadingTutorial(false);
    // In viewer mode UE auto-logs in as the owner, so never surface the login overlay.
    // Ref guard: handleSkip can run before React re-renders hasLoggedInOnce after
    // SDK_Login_Success, which would wrongly reopen the login overlay. Same for
    // pendingLoginRef: a direct login is about to be submitted automatically, so
    // asking for credentials again would be wrong.
    if (
      !hasLoggedInOnceRef.current &&
      !viewerModeRef.current &&
      !pendingLoginRef.current
    ) {
      setLoginVisible(true);
    }
    stopLoadingTimer();
  }, [setLoginVisible, stopLoadingTimer]);

  // ── Direct login (?action=login) ────────────────────────────
  // Schedule the stored, backend-validated credentials for UE after `delayMs`
  // (the grace period UE needs to build its login state — see AUTO_LOGIN_GRACE_MS).
  // Every timeout re-checks pendingLoginRef so credentials cleared in the meantime
  // (login succeeded, or a real error dropped them) cancel the send.
  //
  // A held credential is either a password pair or an OAuth token. The password pair
  // mirrors LoginPage's sendLoginMessages: email first, password 150ms later — UE
  // triggers the PlayFab login on receiving LoginPassword, so the email must already
  // be in place. A token is a single message and needs no sequencing.
  const scheduleAutoLoginSend = useCallback(
    (delayMs) => {
      const sendTimer = setTimeout(() => {
        const creds = pendingLoginRef.current;
        if (!creds) return;
        reportStage("login_sent");
        if (creds.provider) {
          const msg = SOCIAL_LOGIN[creds.provider].ueMessage(creds.token);
          sendMessage(msg);
          console.log(`[${creds.provider}] SENT to UE:`, msg);
          return;
        }
        sendMessage(`LoginEmail,${creds.email}`);
        const passwordTimer = setTimeout(() => {
          if (!pendingLoginRef.current) return;
          sendMessage(`LoginPassword,${creds.password}`);
          console.log("Direct login messages sent");
        }, 150);
        autoLoginTimersRef.current.push(passwordTimer);
      }, delayMs);
      autoLoginTimersRef.current.push(sendTimer);
    },
    [sendMessage, reportStage]
  );

  const maybeTriggerAutoLogin = useCallback(() => {
    const creds = pendingLoginRef.current;
    if (!creds || autoLoginSentRef.current) return;
    autoLoginSentRef.current = true;
    autoLoginAttemptsRef.current = 1;
    // 40s safety net: if UE never answers, the "Login timed out" refresh modal
    // appears (stopped by SDK_Login_Success via stopTimer, like a manual login).
    startTimer();
    // Wait out whatever remains of the post-RenderNow_Loaded grace window: the
    // auto-trigger at load waits the full period, a user submit a few seconds
    // later waits only the remainder, and a submit long after load sends at once.
    const sinceLoaded = loadedAtRef.current ? Date.now() - loadedAtRef.current : 0;
    scheduleAutoLoginSend(Math.max(0, AUTO_LOGIN_GRACE_MS - sinceLoaded));
    clarityEvent("direct_login_auto_submit");
  }, [scheduleAutoLoginSend, startTimer]);

  /**
   * UE is ready to be used: unblock the loading screen and release anything that was
   * waiting on it. Reached either by UE's own RenderNow_Loaded broadcast or, when that
   * broadcast was discarded because it beat our data channel open, by the fallback
   * timer (see UE_LOADED_FALLBACK_MS).
   */
  const markUeReady = useCallback(
    (source) => {
      // Every (re-)emission marks a freshly initialized UE login state — restart
      // the auto-login grace clock from here (a reconnected instance needs the
      // same settling time as the first one).
      loadedAtRef.current = Date.now();
      // UE announcing itself is proof the stream is live, so close the auto-start
      // gate. Several paths call play() directly without touching this ref (the
      // loading-screen button, handleSkip, the reconnect buttons), which used to
      // leave it false — a later requestStream() then re-ran the effect and, because
      // the SDK had already connected once, issued stream.reconnect(). That tore
      // down the data channel mid-flight: the Google trigger went out and UE's
      // open_url reply landed on a dead channel.
      autoStartedRef.current = true;
      const firstTime = !loadedMessageReceivedRef.current;
      if (firstTime) {
        loadedMessageReceivedRef.current = 1;
        setCanSkipVideo(true);
        setConnectionSuccessLoadingApplication(false);
        stopLoadingTimer();
        clarityEvent(source === "fallback" ? "ue_loaded_fallback_used" : "ue_app_loaded");
      }
      // Safety-net resend: guarantees UE gets the viewer-mode message even if the
      // data channel opened before the share token finished validating.
      maybeSendViewerMode();
      // Direct login: UE is ready, submit the stored, already-validated
      // credentials (no-op unless ?action=login stashed some).
      maybeTriggerAutoLogin();
      return firstTime;
    },
    [
      setCanSkipVideo,
      setConnectionSuccessLoadingApplication,
      stopLoadingTimer,
      maybeSendViewerMode,
      maybeTriggerAutoLogin,
    ]
  );

  // Read through a ref so the fallback timer below never restarts just because one of
  // markUeReady's dependencies got a new identity on a re-render — a timer that keeps
  // resetting is a timer that never fires, which is exactly the dead end we're fixing.
  const markUeReadyRef = useRef(markUeReady);
  useEffect(() => { markUeReadyRef.current = markUeReady; }, [markUeReady]);

  // The fallback itself. Both conditions matter: `streaming` means frames are being
  // decoded, `channelOpen` means UE can hear us — together they say the session is
  // genuinely healthy, so a missing RenderNow_Loaded is a lost message rather than an
  // application that never started.
  useEffect(() => {
    if (psStatus !== "streaming" || !channelOpen) return undefined;
    if (loadedMessageReceivedRef.current) return undefined;
    const timer = setTimeout(() => {
      if (loadedMessageReceivedRef.current) return;
      console.warn(
        `[arcware] RenderNow_Loaded not seen ${UE_LOADED_FALLBACK_MS}ms after the stream ` +
          "became usable — unblocking on transport readiness instead"
      );
      markUeReadyRef.current?.("fallback");
    }, UE_LOADED_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [psStatus, channelOpen]);

  // Shared tail of every ?action=login sign-in, whatever proved the identity. By this
  // point the backend has confirmed it, so: hold the credential, swap the form for the
  // loading screen, and start the stream — this submit is the play button (see the
  // auto-start notes above). The credential itself reaches UE later, once it reports
  // itself ready, which is the whole point of holding it: nothing is ever fired at a
  // data channel that isn't open yet.
  // `email` and `playfabBody` are null for Google: its authorization code can't be
  // inspected without spending it, so identity only becomes known once UE replies
  // with `SDK_Login_Success,<email>`, which fills both in via applyResolvedIdentity.
  const beginHeldLogin = useCallback(
    (email, credential, playfabBody) => {
      pendingLoginRef.current = credential;
      autoLoginSentRef.current = false;
      if (email) localStorage.setItem("userEmail", email);
      setAutoLoginPending(true);
      setLoginVisible(false);
      setLoginLoading(false);
      requestStream();
      claritySet("direct_login_method", credential.provider ?? "password");
      clarityEvent("direct_login_validated");
      // Same call LoginPage makes on a normal login: populates playfabData and the
      // admin flag (a 200 means the email is in the admin registry).
      if (playfabBody) {
        axios
          .post(`${API_BASE_URL}/playerdetailsFrontend`, playfabBody)
          .then((res) => {
            setPlayfabData(res.data);
            setIsAdmin(true);
          })
          .catch(() => {
            setPlayfabData([]);
            setIsAdmin(false);
          });
      }
      // If UE finished loading while the user was signing in, trigger the login now
      // (still behind the grace period — RenderNow_Loaded may have JUST arrived);
      // otherwise the RenderNow_Loaded handler fires it.
      if (loadedMessageReceivedRef.current) maybeTriggerAutoLogin();
    },
    [maybeTriggerAutoLogin, setLoginVisible, setPlayfabData, setIsAdmin, requestStream]
  );

  // Identity reported by UE on `SDK_Login_Success,<email>`. For Google this is the ONLY
  // source of the address: the browser hands UE an authorization code it can't inspect,
  // so nothing upstream ever learns who signed in. `userEmail` is what the Share-link
  // button (handleShare) and the 3D upload both read, so without this they stay broken
  // for Google users.
  //
  // Deliberately does NOT fetch admin data: /playerdetailsFrontend authenticates the
  // caller with a Google/Microsoft token or a password, and a bare email proves nothing.
  // Populating the admin panel for Google logins needs UE to return something verifiable
  // (a PlayFab session ticket) plus a backend route to check it — not done here.
  const applyResolvedIdentity = useCallback((email) => {
    const trimmed = email?.trim();
    if (!trimmed) return;
    localStorage.setItem("userEmail", trimmed);
    claritySet("login_email_source", "ue_reported");
  }, []);

  // Tab held open for UE's Google consent URL. It has to be opened during the button
  // click, because that is the only moment a popup is authorised: UE's `open_url`
  // arrives in an async data-channel callback, and opening it there is a silent
  // no-op in every browser. It therefore exists for a beat before it has anywhere to
  // go, so it gets a holding page rather than sitting there blank and looking broken.
  const googleAuthTabRef = useRef(null);

  // Armed when the trigger is handed over, cleared the moment UE answers. Without it
  // a trigger that never reaches UE (stream not up, UE not listening) leaves the tab
  // on the holding page and the overlay spinning forever, with nothing to report.
  const googleAuthTimeoutRef = useRef(null);

  const clearGoogleAuthTimeout = useCallback(() => {
    if (googleAuthTimeoutRef.current) {
      clearTimeout(googleAuthTimeoutRef.current);
      googleAuthTimeoutRef.current = null;
    }
  }, []);

  const closeGoogleAuthTab = useCallback(() => {
    const tab = googleAuthTabRef.current;
    googleAuthTabRef.current = null;
    // Only ever called on a terminal outcome — login succeeded, UE reported an error,
    // or we timed out — so the sign-in is over whichever tab it is sitting on and the
    // user has no reason to keep it. close() works on a window we opened even once it
    // is cross-origin; reading its location would not, so don't.
    if (!tab || tab.closed) return;
    try {
      tab.close();
    } catch (_) {
      // Nothing more we can do; a stray tab is harmless.
    }
  }, []);

  // "Continue with Google" — the browser runs no OAuth of its own, it just asks UE to
  // start and provides the window Google must be rendered in (UE is on a remote GPU;
  // anything it opens locally is invisible to the user). The trigger itself is held
  // like any other credential so it can't be fired before UE reports ready.
  const handleGoogleTrigger = useCallback(() => {
    setError("");
    setSuccess("");
    let tab = null;
    try {
      tab = window.open("", "_blank");
      // about:blank inherits our origin, so the placeholder can explain itself while
      // it waits for UE's URL. Guarded: a blocked popup returns null.
      tab?.document?.write(GOOGLE_TAB_HOLDING_PAGE);
      tab?.document?.close();
    } catch (_) {
      tab = null;
    }
    googleAuthTabRef.current = tab;
    if (!tab) console.warn("[google] popup blocked — no tab to redirect");

    // The single most useful line when this goes wrong: it says whether we are still
    // waiting on UE to announce itself, or have already handed the trigger over.
    console.log(
      loadedMessageReceivedRef.current
        ? "[google] trigger will be sent now (RenderNow_Loaded already seen)"
        : "[google] trigger QUEUED — waiting for RenderNow_Loaded from UE"
    );

    clearGoogleAuthTimeout();
    googleAuthTimeoutRef.current = setTimeout(() => {
      googleAuthTimeoutRef.current = null;
      if (!pendingLoginRef.current) return;
      console.warn(
        `[google] no open_url from UE after ${GOOGLE_AUTH_TIMEOUT_MS}ms — giving up`
      );
      pendingLoginRef.current = null;
      autoLoginSentRef.current = false;
      closeGoogleAuthTab();
      setAutoLoginPending(false);
      setLoginVisible(true);
      setError("Google sign-in didn't start. Please try again.");
      clarityEvent("google_trigger_timeout");
    }, GOOGLE_AUTH_TIMEOUT_MS);

    beginHeldLogin(null, { provider: "google" }, null);
  }, [
    beginHeldLogin,
    setError,
    setSuccess,
    clearGoogleAuthTimeout,
    closeGoogleAuthTab,
    setLoginVisible,
  ]);

  // LoginPage's 30s safety net fired: UE never answered a login / account request.
  // Record what every layer looked like at that moment (the "steps away, comes back,
  // login times out" reports have no other trace), and if the transport is down,
  // replace the dead-end "please try again" — which can't work on a dead stream —
  // with the Connection Lost modal. Stopping the 40s login timer keeps App.js from
  // re-titling that modal "Login timed out" ten seconds later.
  const handleLoginTimeout = useCallback(
    (kind) => {
      const health = getConnectionHealth();
      console.warn(`[RenderNow] ${kind} got no reply from UE within 30s`, health);
      claritySet("login_timeout_health", JSON.stringify(health));
      clarityEvent(`${kind}_timeout`);
      const transportUp =
        health.status === "streaming" &&
        health.channelOpen &&
        (health.ice == null || health.ice === "connected" || health.ice === "completed");
      if (transportUp) return;
      stopTimer();
      setRefreshModalReason((r) => (r === "afk" ? r : "stream"));
      setRefreshRequiredModalVisible(true);
    },
    [getConnectionHealth, stopTimer, setRefreshModalReason, setRefreshRequiredModalVisible]
  );

  // LoginPage submit handler for ALL ?action=login submits (pre- and post-load):
  // validate the credentials against the backend (PlayFab) immediately — so bad
  // input gets a friendly, specific error without ever touching UE — then hold
  // them until UE is ready (RenderNow_Loaded + grace window).
  const handleDirectLogin = useCallback(
    async (email, password) => {
      setError("");
      setSuccess("");
      try {
        await axios.post(`${API_BASE_URL}/validateLoginCredentials`, {
          email,
          password,
        });
      } catch (e) {
        const data = e.response?.data;
        if (data?.userNotFound) {
          setError(
            "We couldn't find a password account for this email. If you signed up with Google, use Continue with Google. Otherwise, click 'Create New Account' below."
          );
        } else {
          setError(
            data?.message || "Could not verify your credentials. Please try again."
          );
        }
        setLoginLoading(false);
        return;
      }
      beginHeldLogin(email, { email, password }, { email, password });
    },
    [beginHeldLogin]
  );

  // Same contract as handleDirectLogin, for the Google/Microsoft buttons. LoginPage
  // runs the OAuth dance and hands us the raw token; we exchange it for the account's
  // email and then hold it exactly like a password pair.
  //
  // Previously these bypassed the held-credentials path entirely and pushed
  // `Login_Token` down the data channel the instant the popup closed — which only ever
  // worked because the stream had been connecting since page load. It raced UE's
  // startup even then, and became a guaranteed no-op once ?action=login stopped
  // auto-starting: the token was sent before the stream existed, and the user waited
  // out a 30s timeout for a reply that could never come.
  const handleDirectSocialLogin = useCallback(
    async (provider, token) => {
      const cfg = SOCIAL_LOGIN[provider];
      if (!cfg) return;
      setError("");
      setSuccess("");
      // Google (no validatePath) hands us a single-use authorization code that only
      // PlayFab may redeem, so there is nothing to pre-validate and no email to
      // resolve — hold it and let UE report the identity back.
      if (!cfg.validatePath) {
        beginHeldLogin(null, { provider, token }, null);
        return;
      }
      let email;
      try {
        const res = await axios.post(
          `${API_BASE_URL}/${cfg.validatePath}`,
          cfg.validateBody(token)
        );
        email = res.data?.email;
      } catch (e) {
        setError(
          e.response?.data?.message ||
            `${cfg.label} sign-in failed. Please try again.`
        );
        setLoginLoading(false);
        return;
      }
      // A token the backend accepts but can't resolve to an account would otherwise be
      // held and sent to UE as an empty identity.
      if (!email) {
        setError(`We couldn't read your ${cfg.label} account. Please try again.`);
        setLoginLoading(false);
        return;
      }
      beginHeldLogin(email, { provider, token }, cfg.playfabBody(email, token));
    },
    [beginHeldLogin]
  );

  // "Create New Account" from the direct-login popup: hand the user to the same
  // signup intake flow ?action=signup runs, so nobody coming from the website login
  // button can skip the New User Intake form. The URL is rewritten in place — the
  // stream keeps connecting, and the post-intake login overlay opens in "New
  // Account" mode exactly like a genuine signup visit.
  const handleContinueToSignup = useCallback(() => {
    setError("");
    setSuccess("");
    // pendingLoginRef.current = null;
    // autoLoginSentRef.current = false;
    // setAutoLoginPending(false);
    // setLoginVisible(false);
    // setSignupIntakeVisible(true);
    // clarityEvent("direct_login_signup_redirect");
    window.location.replace("/render?action=signup");
  }, [history, setLoginVisible]);

  // After the intake form is submitted, wait until the app is actually ready before
  // showing the login page — mirrors the normal handleSkip flow.
  useEffect(() => {
    if (signupFormSubmitted && canSkipVideo && !hasLoggedInOnceRef.current) {
      handleSkip();
    }
  }, [signupFormSubmitted, canSkipVideo, hasLoggedInOnce, handleSkip]);

  // Ends the post-Google-login hold. hasLoggedInOnce waits for this too, because
  // LoadingScreen shows its "Reconnecting…" panel once that flag is set.
  const revealAfterGoogleLogin = useCallback(() => {
    clearTimeout(googleRevealTimerRef.current);
    googleRevealTimerRef.current = null;
    setAutoLoginPending(false);
    setHasLoggedInOnce?.(true);
  }, [setHasLoggedInOnce]);

  const resetGoogleLink = useCallback(() => {
    clearTimeout(googleLinkTimerRef.current);
    googleLinkTimerRef.current = null;
    googleLinkActiveRef.current = false;
    googleLinkLoginDoneRef.current = false;
  }, []);

  // End of the link step, either way. The password login has already worked by the time
  // UE links, so a failed link still lets the user in; it only costs them the Google
  // shortcut next time. UE's failure text is logged, never shown.
  const finishGoogleLink = useCallback(
    (failureReason) => {
      const loginDone = googleLinkLoginDoneRef.current;
      resetGoogleLink();
      clearGoogleAuthTimeout();
      closeGoogleAuthTab();
      setGoogleLinkEmail("");
      if (failureReason) {
        console.warn("[google] link failed:", failureReason);
        clarityEvent("google_link_failed");
        setGoogleLinkNotice(
          "We couldn't link your Google account this time. You can keep using your password and try Google again later."
        );
        clearTimeout(googleLinkNoticeTimerRef.current);
        googleLinkNoticeTimerRef.current = setTimeout(() => setGoogleLinkNotice(""), 10000);
      } else {
        clarityEvent("google_link_success");
      }
      // Login not reported yet: SDK_Login_Success will reveal the editor itself.
      if (!loginDone) return;
      setAutoLoginPending(false);
      setHasLoggedInOnce?.(true);
    },
    [resetGoogleLink, clearGoogleAuthTimeout, closeGoogleAuthTab, setHasLoggedInOnce]
  );

  // Backstop for a link result that never comes (UE not sending it, user abandoning the
  // Google tab). Same budget as the consent wait; the login itself already succeeded.
  const armGoogleLinkTimeout = useCallback(() => {
    clearTimeout(googleLinkTimerRef.current);
    googleLinkTimerRef.current = setTimeout(
      () => finishGoogleLink("timeout"),
      GOOGLE_CONSENT_TIMEOUT_MS
    );
  }, [finishGoogleLink]);

  // Submit of the link form. The Google tab for the link step is opened HERE, inside the
  // click: UE's open_url arrives later in a data-channel callback, where a new window
  // would be blocked. The earlier tab is reused when the user hasn't closed it. Password
  // is checked with the backend first, as for ?action=login, so a typo never reaches UE.
  const handleGoogleLinkLogin = useCallback(
    async (email, password) => {
      setError("");
      setSuccess("");
      let tab = googleAuthTabRef.current;
      let openedHere = false;
      if (!tab || tab.closed) {
        try {
          tab = window.open("", "_blank");
          tab?.document?.write(GOOGLE_TAB_HOLDING_PAGE);
          tab?.document?.close();
        } catch (_) {
          tab = null;
        }
        googleAuthTabRef.current = tab;
        openedHere = Boolean(tab);
      }
      try {
        await axios.post(`${API_BASE_URL}/validateLoginCredentials`, { email, password });
      } catch (e) {
        if (openedHere) closeGoogleAuthTab();
        setError(
          e.response?.data?.userNotFound
            ? "We couldn't find a password account for this email. Press Cancel and use Continue with Google instead."
            : e.response?.data?.message || "Could not verify your credentials. Please try again."
        );
        setLoginLoading(false);
        return;
      }
      googleLinkActiveRef.current = true;
      googleLinkLoginDoneRef.current = false;
      clarityEvent("google_link_password_submitted");
      beginHeldLogin(email, { email, password }, { email, password });
    },
    [beginHeldLogin, closeGoogleAuthTab]
  );

  // "Cancel" on the link form: back to the normal login form.
  const handleGoogleLinkCancel = useCallback(() => {
    resetGoogleLink();
    closeGoogleAuthTab();
    setGoogleLinkEmail("");
    setError("");
    setSuccess("");
    clarityEvent("google_link_cancelled");
  }, [resetGoogleLink, closeGoogleAuthTab]);

  const handleMessage = useCallback(
    (data) => {
      if (typeof data !== "string" || data.length === 0) return;
      console.log("-----UE Message Received:", data);

      // The only JSON UE speaks; everything else on this channel is a plain string
      // (we own the raw "ue-response" listener — see useArcwareStream). Sent when UE
      // has built its Google consent URL and needs it shown to the user. Both `cmd`
      // and `type` are accepted, matching the UE side's own handler.
      if (data[0] === "{") {
        let payload = null;
        try {
          payload = JSON.parse(data);
        } catch (_) {
          console.error("[ue] unparseable JSON message:", data);
          return;
        }
        console.log("[ue] JSON message:", payload);
        if (payload?.cmd === "open_url" || payload?.type === "open_url") {
          clearGoogleAuthTimeout();
          const url = payload.url;
          if (!url) {
            console.warn("[google] open_url received with no `url` field:", payload);
            return;
          }
          console.log("[google] open_url received, redirecting tab ->", url);
          // Hand off from the normal budgets to a window sized for an external OAuth
          // round trip. BOTH timers have to stop: the 40s login one, and the 90s
          // loading one — LoadingScreen keeps the latter armed for as long as
          // hasLoggedInOnce is false, which is exactly the stretch the user spends
          // over on Google. Left running it expires mid-sign-in and raises the
          // "Something went wrong with the connection" modal over a perfectly healthy
          // stream, and the user refreshes away a session that was still working.
          stopTimer();
          stopLoadingTimer();
          if (googleLinkActiveRef.current && googleLinkLoginDoneRef.current) {
            armGoogleLinkTimeout();
          }
          googleAuthTimeoutRef.current = setTimeout(() => {
            googleAuthTimeoutRef.current = null;
            if (!pendingLoginRef.current) return;
            console.warn(
              `[google] no SDK_Login_Success after ${GOOGLE_CONSENT_TIMEOUT_MS}ms — giving up`
            );
            pendingLoginRef.current = null;
            autoLoginSentRef.current = false;
            closeGoogleAuthTab();
            setAutoLoginPending(false);
            setLoginVisible(true);
            setError("Google sign-in didn't complete. Please try again.");
            clarityEvent("google_consent_timeout");
          }, GOOGLE_CONSENT_TIMEOUT_MS);
          // KEEP the handle for the whole flow. UE opens the browser TWICE — once for
          // mode=check, then again for mode=code — and by that second message the
          // click that authorised a popup is long gone (the user has been off in the
          // Google tab). A fresh window.open() there is silently blocked, which is why
          // the page only ever appeared once. Navigating a window we already own is
          // always allowed, even cross-origin, and reuses one tab for the whole
          // sign-in instead of stacking up two. Deliberately no "noopener": that makes
          // window.open return null, so we would lose the handle for the next trip.
          const tab = googleAuthTabRef.current;
          if (tab && !tab.closed) {
            tab.location.href = url;
            try { tab.focus(); } catch (_) {}
          } else {
            console.warn(
              "[google] no tab to reuse (closed, or opened outside a click) — this open may be popup-blocked"
            );
            try {
              googleAuthTabRef.current = window.open(url, "_blank");
            } catch (_) {
              googleAuthTabRef.current = null;
            }
          }
          clarityEvent("ue_open_url");
        } else {
          console.warn(
            "[ue] JSON message ignored — expected cmd/type of \"open_url\":",
            payload
          );
        }
        return;
      }

      if (data.startsWith("Veo_Link:")) {
        const url = data.slice("Veo_Link:".length);
        setVeoLinkUrl(url);
        clarityEvent("ue_veo_link_received");
        return;
      }

      if (data.startsWith("video_download_url:") || data.startsWith("ideo_download_url:")) {
        const prefix = data.startsWith("video_download_url:") ? "video_download_url:" : "ideo_download_url:";
        const s3Url = data.slice(prefix.length);
        setVideofileone(s3Url);
        clarityEvent("ue_video_download_ready");
        return;
      }

      // Scene export (Export Scene -> OBJ single file / GLTF zip). UE uploads the
      // result to S3 and sends the link here; we download it straight away rather
      // than surfacing a link, since the S3 objects aren't meant to be kept around.
      // The leading-character-dropped variant is accepted for the same reason as
      // the video/render messages below — UE occasionally truncates the first byte.
      if (data.startsWith("export_download_url:") || data.startsWith("xport_download_url:")) {
        const prefix = data.startsWith("export_download_url:")
          ? "export_download_url:"
          : "xport_download_url:";
        const s3Url = data.slice(prefix.length).trim();
        setExportDownloadUrl(s3Url);
        clarityEvent("ue_scene_export_download_ready");
        return;
      }

      if (data.startsWith("render_download_url:") || data.startsWith("ender_download_url:")) {
        const prefix = data.startsWith("render_download_url:")
          ? "render_download_url:"
          : "ender_download_url:";
        const imageUrl = data.slice(prefix.length);
        setRenderDownloadUrl(imageUrl);
        clarityEvent("ue_render_image_download_ready");
        return;
      }

      if (data.length < 200) {
        let successful = false;

        // UE may append the signed-in address: `SDK_Login_Success,<email>`. That is the
        // only way we learn the identity behind a Google login now that the browser
        // never redeems the authorization code (see SOCIAL_LOGIN.google). The bare form
        // is still accepted so UE can adopt the email at its own pace.
        if (data === "SDK_Login_Success" || data.startsWith("SDK_Login_Success,")) {
          const reportedEmail = data.startsWith("SDK_Login_Success,")
            ? data.slice("SDK_Login_Success,".length).trim()
            : "";
          if (reportedEmail) applyResolvedIdentity(reportedEmail);
          // The password login of a Google link succeeds before the link itself: the
          // Google tab must stay open for UE's next open_url.
          const linkInProgress = googleLinkActiveRef.current;
          if (!linkInProgress) {
            clearGoogleAuthTimeout();
            closeGoogleAuthTab();
          }
          stopTimer();
          stopLoadingTimer();
          hasLoggedInOnceRef.current = true;
          // Signup fully complete — clear the intake-submitted flag so a later signup in
          // this same session starts clean instead of skipping the intake form.
          clearIntakeSubmitted();
          // Checked before pendingLoginRef is cleared. The timer half covers a repeated
          // SDK_Login_Success, which must not cut a running hold short.
          const holdLoadingScreen =
            pendingLoginRef.current?.provider === "google" || googleRevealTimerRef.current;
          // Direct login complete — drop the stored credentials and the wait overlay.
          pendingLoginRef.current = null;
          setLoginVisible(false);
          setShowLoadingTutorial(false);
          setSignupNewAccountDone(true);
          setSuccess("");
          setUserLoggedIn(true);
          setConnectionSuccessLoadingApplication(false);
          if (linkInProgress) {
            // Stay on the loading screen until Google_Link_Success / _Failed.
            googleLinkLoginDoneRef.current = true;
            armGoogleLinkTimeout();
          } else if (!holdLoadingScreen) {
            setAutoLoginPending(false);
            setHasLoggedInOnce?.(true);
          } else if (!googleRevealTimerRef.current) {
            googleRevealTimerRef.current = setTimeout(
              revealAfterGoogleLogin,
              GOOGLE_POST_LOGIN_HOLD_MS
            );
          }
          setRefreshRequiredModalVisible(false);
          setRefreshModalReason?.(null);
          clarityEvent("ue_login_success");
          reportStage("login_success");
          successful = true;
        }

        // UE found a password account for the Google email. The loading screen would hide
        // any prompt UE drew itself, so the form is ours.
        if (data.startsWith("Google_Account_Found")) {
          const foundEmail = data.startsWith("Google_Account_Found,")
            ? data.slice("Google_Account_Found,".length).trim()
            : "";
          clearGoogleAuthTimeout();
          stopTimer();
          stopLoadingTimer();
          pendingLoginRef.current = null;
          autoLoginSentRef.current = false;
          resetGoogleLink();
          setAutoLoginPending(false);
          setLoginLoading(false);
          setSuccess("");
          setGoogleLinkEmail(foundEmail);
          setError(
            foundEmail
              ? ""
              : "An account with this email already exists. Please log in with your password."
          );
          setLoginVisible(true);
          clarityEvent("google_account_found");
          successful = true;
        }

        if (data === "Google_Link_Success" || data.startsWith("Google_Link_Failed")) {
          finishGoogleLink(
            data === "Google_Link_Success"
              ? null
              : data.slice("Google_Link_Failed".length).replace(/^,/, "").trim() || "unspecified"
          );
          successful = true;
        }

        if (data === "RenderNow_Editor_Ready") {
          if (googleRevealTimerRef.current) revealAfterGoogleLogin();
          clarityEvent("ue_editor_ready");
          successful = true;
        }

        if (data === "RenderNow_Loaded") {
          reportStage("ue_loaded");
          if (markUeReady("message")) successful = true;
        }

        if (data === "FullScreen_Toggle") {
          if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen?.();
          } else {
            document.exitFullscreen?.();
          }
          clarityEvent("ue_fullscreen_toggle");
        }

        if (data === "Add_Image_PopUp") {
          setImgbbContainerVisible(true);
          clarityEvent("ue_image_upload_modal_opened");
        }
        if (data === "Add_3DFile_PopUp" || data === "3dmodel:open") {
          setUpload3DContainerVisible(true);
          clarityEvent("ue_3d_upload_modal_opened");
        }
        if (data === "image_to_3d_trigger") {
          setImageTo3DContainerVisible?.(true);
          clarityEvent("ue_image_to_3d_modal_opened");
        }

        if (data === "Render_Image_Triggered") {
          setRenderStarted(true);
          setDownloadVisible(true);
          clarityEvent("ue_render_triggered");
        }

        if (data === "Password reset has been sent to your email successfully") {
          setSuccess(data);
          successful = true;
        }
        // Only treat this as a user-facing "success message" (which drives the
        // "Account Created" confirmation UI) if it hasn't already been handled as
        // a login/loaded/reset success above. Without this guard, a normal login —
        // which sends "SDK_Login_Success" — matches `includes("Success")` and
        // wrongly sets the success state, surfacing the account-created message on
        // an ordinary login.
        if (!successful && data.includes("Success")) {
          setSuccess(data);
          setSignupNewAccountDone(true);
          successful = true;
        }
        if (data.includes("https://api.rendernow.co/images/")) {
          setImagefileone(data);
          setRenderStarted(false);
          setDownloadVisible(true);
          clarityEvent("ue_render_image_ready");
        }


        if (!successful) {
          setSuccess("");
          if (data !== "RenderNow_Loaded") {
            // PlayFab's "Invalid input parameters … field is required" report means
            // UE ran the login with EMPTY fields: our credentials arrived before its
            // login state existed, so no real auth attempt happened. Resend them
            // after another grace period instead of surfacing the error, keeping
            // the "logging you in" overlay up. Capped by AUTO_LOGIN_MAX_ATTEMPTS;
            // any other error (or exhausted retries) falls through to the reopen
            // path below.
            const emptyCredsError =
              data.includes("Invalid input parameters") ||
              data.includes("field is required");
            // Google sign-in is running in the other tab, so the login form must not
            // come back for UE chatter: it would sit there (often with no error text)
            // until SDK_Login_Success hides it again, and Enter on it re-fires the
            // trigger via the empty-credentials retry below. Only a message that
            // reads as a real failure ends the wait; the consent timeout is the
            // backstop for the rest.
            if (
              pendingLoginRef.current?.provider === "google" &&
              !/error|fail|denied|cancel|not linked/i.test(data)
            ) {
              claritySet("ue_google_pending_message", data);
              clarityEvent("google_pending_ue_message_ignored");
              return;
            }
            if (
              pendingLoginRef.current &&
              emptyCredsError &&
              autoLoginAttemptsRef.current < AUTO_LOGIN_MAX_ATTEMPTS
            ) {
              autoLoginAttemptsRef.current += 1;
              scheduleAutoLoginSend(AUTO_LOGIN_GRACE_MS);
              clarityEvent("direct_login_empty_creds_retry");
              return;
            }
            // In direct-login mode every real login goes through backend validation
            // + the auto-send path, so an empty-credentials report with NOTHING
            // pending is UE-internal noise (its login state initializing) — never
            // user-actionable. Don't paint it into the open form; just tag it.
            if (emptyCredsError && directLoginMode && !pendingLoginRef.current) {
              claritySet("ue_last_unhandled_message", data);
              clarityEvent("ue_spurious_empty_creds_error");
              return;
            }
            // If a direct login was pending or in flight, drop the stored
            // credentials and reopen the login popup so the user actually sees
            // the error instead of a stuck "logging you in" overlay. If the retries
            // above ran out, the raw PlayFab report is developer noise — show a
            // human message instead (the raw text still reaches Clarity below).
            if (pendingLoginRef.current) {
              pendingLoginRef.current = null;
              resetGoogleLink();
              clearGoogleAuthTimeout();
              closeGoogleAuthTab();
              autoLoginSentRef.current = false;
              setAutoLoginPending(false);
              setLoginVisible(true);
              setError(
                emptyCredsError
                  ? "We couldn't log you in automatically. Please try again."
                  : data
              );
            } else {
              setError(data);
            }
            stopTimer();
            stopLoadingTimer();
            // Surfaces the raw UE message as a searchable Clarity tag — without
            // this, an unhandled/error message during the (black) streaming
            // segment is only ever visible in the browser console.
            claritySet("ue_last_unhandled_message", data);
            clarityEvent("ue_unhandled_message");
          }
        }
        if (data !== "RenderNow_Loaded") {
          setResetPasswordLoading(false);
          setLoginLoading(false);
          setNewAccountLoading(false);
        }
      }
    },
    [
      stopTimer,
      stopLoadingTimer,
      setLoginVisible,
      setUserLoggedIn,
      setConnectionSuccessLoadingApplication,
      setHasLoggedInOnce,
      setRefreshRequiredModalVisible,
      setRefreshModalReason,
      setImgbbContainerVisible,
      setUpload3DContainerVisible,
      setImageTo3DContainerVisible,
      setRenderStarted,
      setDownloadVisible,
      setImagefileone,
      setVideofileone,
      setRenderDownloadUrl,
      setExportDownloadUrl,
      setVeoLinkUrl,
      markUeReady,
      reportStage,
      applyResolvedIdentity,
      closeGoogleAuthTab,
      clearGoogleAuthTimeout,
      revealAfterGoogleLogin,
      resetGoogleLink,
      finishGoogleLink,
      armGoogleLinkTimeout,
      scheduleAutoLoginSend,
      directLoginMode,
      setSuccess,
    ]
  );

  // Lets the UE side prove our half of the Google handshake without running UE at all:
  // `rnGoogleTest()` feeds a synthetic open_url straight into the real handler, so if
  // the tab redirects, everything from parsing to popup handling is working and the
  // fault is upstream. Non-production only, alongside window.rnDiag.
  useEffect(() => {
    if (IS_PRODUCTION) return undefined;
    window.rnGoogleTest = (url = "https://example.com") => {
      handleMessageRef.current?.(JSON.stringify({ cmd: "open_url", url }));
      return `Simulated open_url -> ${url}. If a tab was open it should now be there.`;
    };
    return () => { delete window.rnGoogleTest; };
  }, []);

  // Keep the adapter's stable callbacks pointing at the latest handlers.
  useEffect(() => { handleMessageRef.current = handleMessage; }, [handleMessage]);
  useEffect(() => { dataChannelOpenRef.current = maybeSendViewerMode; }, [maybeSendViewerMode]);

  // Explicitly release the Arcware instance when the user closes or leaves the page.
  // stop() -> disconnect() closes the signaling WebSocket + RTCPeerConnection, which is
  // what tells Arcware's server the player left so the instance is reclaimed immediately
  // instead of lingering for minutes on an idle timeout.
  //
  // We listen on BOTH beforeunload and pagehide on purpose: `beforeunload` covers desktop
  // refresh/close, but browsers (mobile Safari/Chrome especially) routinely DON'T fire it
  // when a tab is closed or a backgrounded tab is discarded by the OS — `pagehide` is the
  // reliable signal in those cases. The teardown is idempotent (disconnect() on an already
  // closed connection is a no-op; emitUIInteraction is try/caught), so both firing is safe.
  useEffect(() => {
    const cb = () => {
      // Never queue this one: the page is going away, so a deferred send would only
      // sit in a queue that is about to be garbage — send it now or not at all.
      try { sendMessage("CLOSED_BY_USER", { queue: false }); } catch (_) {}
      try { stop(); } catch (_) {}
    };
    window.addEventListener("beforeunload", cb);
    window.addEventListener("pagehide", cb);
    return () => {
      window.removeEventListener("beforeunload", cb);
      window.removeEventListener("pagehide", cb);
    };
  }, [sendMessage, stop]);

  // Mirror the refresh/close teardown above for SPA navigation away from /render.
  // On a real refresh, `beforeunload` fires while the stream is still live, so UE gets
  // "CLOSED_BY_USER" and we disconnect cleanly. But a client-side route change (e.g. the
  // browser back button) never fires beforeunload — React just unmounts the emulator, and
  // useArcwareStream's cleanup disconnects the transport WITHOUT telling UE the user left.
  // Listen for the route change and run the same teardown here, while the data channel is
  // still open (history listeners fire before React commits the unmount), so leaving /render
  // by navigation releases the session exactly like a refresh does.
  useEffect(() => {
    const unlisten = history.listen((loc) => {
      if (loc.pathname === "/render") return; // staying on /render (e.g. query change)
      // As above: we're leaving /render, so there is no later moment to flush to.
      try { sendMessage("CLOSED_BY_USER", { queue: false }); } catch (_) {}
      try { stop(); } catch (_) {}
    });
    return unlisten;
  }, [history, sendMessage, stop]);

  // Prevent Tab key from cycling browser focus to UI buttons (hamburger, overlay buttons)
  // while the stream is the main view. Allow Tab to work normally inside open modals/menus.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;
      if (
        loginVisible ||
        loginVisibleAdmin ||
        upload3DContainerVisible ||
        imgbbContainerVisible ||
        imageTo3DContainerVisible ||
        menuOpen ||
        reportIssueOpen ||
        refreshRequiredModalVisible ||
        signupIntakeVisible
      )
        return;
      e.preventDefault();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    loginVisible,
    loginVisibleAdmin,
    upload3DContainerVisible,
    imgbbContainerVisible,
    imageTo3DContainerVisible,
    menuOpen,
    reportIssueOpen,
    refreshRequiredModalVisible,
    signupIntakeVisible,
  ]);

  // Disable page scroll while cursor is inside the stream container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const disableScroll = () => {
      document.body.style.position = "fixed";
      document.body.style.top = "0px";
      document.body.style.left = "0px";
      document.body.style.right = "0px";
      document.body.style.overflowY = "scroll";
    };
    const enableScroll = () => {
      document.body.style.position = "";
    };

    container.addEventListener("mouseenter", disableScroll);
    container.addEventListener("mouseleave", enableScroll);
    return () => {
      container.removeEventListener("mouseenter", disableScroll);
      container.removeEventListener("mouseleave", enableScroll);
    };
  }, [containerRef]);

  // Hide Tawk when menu opens
  useEffect(() => {
    if (!menuOpen) return;
    try {
      window.Tawk_API?.hideWidget?.();
    } catch (_) {}
  }, [menuOpen]);


  // Send IMGBB_MODAL_CLOSE to UE whenever the image upload widget is dismissed
  // (fires on both cancel and after success close)
  const imgbbMountedRef = useRef(false);
  useEffect(() => {
    if (!imgbbMountedRef.current) {
      imgbbMountedRef.current = true;
      return;
    }
    if (!imgbbContainerVisible) {
      sendMessage("IMGBB_MODAL_CLOSE");
    }
  }, [imgbbContainerVisible, sendMessage]);

  // ── Status-driven interrupt handling (replaces Interlucent's status MutationObserver) ──
  // After login, show the full-page interrupt only for terminal statuses
  // (error / failed / ended), not while (re)connecting.
  useEffect(() => {
    const s = psStatus;

    if (s === "streaming") {
      setHasEverStreamed(true);
      setRefreshRequiredModalVisible(false);
      setRefreshModalReason(null);
    }

    // No GPU worker available: Arcware puts us in a streaming queue when no instance is
    // free. If we stay queued for 180s, surface the same "no worker" interrupt as before.
    if (s === "queued") {
      if (!queueTimeoutRef.current) {
        queueTimeoutRef.current = setTimeout(() => {
          queueTimeoutRef.current = null;
          setNoWorkerAvailable(true);
          setShowLoadingTutorial(false);
          clarityEvent("ue_stuck_queued_no_worker");
        }, 180000);
      }
    } else {
      if (queueTimeoutRef.current) {
        clearTimeout(queueTimeoutRef.current);
        queueTimeoutRef.current = null;
      }
      setNoWorkerAvailable(false);
    }

    // If the user has ever streamed and we're back in a pending state, this is a
    // reconnect attempt. Give it 90 s before surfacing the "Connection lost" modal.
    if (hasEverStreamed && PENDING_RECONNECT_STATUSES.has(s)) {
      if (!reconnectTimeoutRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          // Never downgrade an AFK-timeout modal to the generic "Connection lost".
          setRefreshModalReason((r) => (r === "afk" ? r : "stream"));
          setRefreshRequiredModalVisible(true);
          setShowLoadingTutorial(false);
        }, 90_000);
      }
    } else if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Terminal failure only. Debounce by 1.5s so transient terminal blips during a
    // refresh teardown or a quick auto-recover don't flash the "Connection lost" modal.
    // Gate on hasEverStreamed (not just hasLoggedInOnce): the stream can die while the
    // user is still on the Continue/login screens (e.g. they walked away after Play),
    // and without the modal their only symptom is a login that times out against a
    // dead data channel ("Login is taking longer than expected").
    if ((hasLoggedInOnce || hasEverStreamed) && TERMINAL_INTERRUPT_STATUSES.has(s)) {
      if (!terminalTimeoutRef.current) {
        terminalTimeoutRef.current = setTimeout(() => {
          terminalTimeoutRef.current = null;
          setRefreshModalReason((r) => (r === "afk" ? r : "stream"));
          setRefreshRequiredModalVisible(true);
        }, 1500);
      }
    } else if (terminalTimeoutRef.current) {
      clearTimeout(terminalTimeoutRef.current);
      terminalTimeoutRef.current = null;
    }
  }, [psStatus, hasEverStreamed, hasLoggedInOnce, setRefreshRequiredModalVisible, setRefreshModalReason]);

  // Clear any pending interrupt timers on unmount.
  useEffect(() => () => {
    if (queueTimeoutRef.current) clearTimeout(queueTimeoutRef.current);
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    if (terminalTimeoutRef.current) clearTimeout(terminalTimeoutRef.current);
  }, []);

  // ── AFK inactivity handling ─────────────────────────────────
  // The SDK closed the session because the AFK countdown expired. Show the
  // dedicated inactivity modal (not the generic "Connection lost") so users —
  // and Clarity recordings — can tell an idle timeout from a real disconnect.
  useEffect(() => {
    if (!afkTimedOut) return;
    setRefreshModalReason("afk");
    setRefreshRequiredModalVisible(true);
    setShowLoadingTutorial(false);
  }, [afkTimedOut, setRefreshModalReason, setRefreshRequiredModalVisible]);

  // While the AFK countdown is showing, any real user activity dismisses it.
  // (The SDK pauses its idle-watch during the countdown, so without this a user
  // who came back and kept working would still be dropped when it expired.)
  const afkWarningActive = Boolean(afkWarning);
  useEffect(() => {
    if (!afkWarningActive) return;
    clarityEvent("ue_afk_warning_shown");
    const dismiss = () => dismissAfkWarning();
    const opts = { passive: true };
    window.addEventListener("mousemove", dismiss, opts);
    window.addEventListener("mousedown", dismiss, opts);
    window.addEventListener("keydown", dismiss, opts);
    window.addEventListener("touchstart", dismiss, opts);
    window.addEventListener("wheel", dismiss, opts);
    return () => {
      window.removeEventListener("mousemove", dismiss, opts);
      window.removeEventListener("mousedown", dismiss, opts);
      window.removeEventListener("keydown", dismiss, opts);
      window.removeEventListener("touchstart", dismiss, opts);
      window.removeEventListener("wheel", dismiss, opts);
    };
  }, [afkWarningActive, dismissAfkWarning]);

  // ── Frozen last frame on disconnect ─────────────────────────
  // Clarity can't record <video> content, so recordings are black while
  // streaming. When the session drops, the stream hook publishes a JPEG of the
  // last decoded frame and we overlay it as a plain <img> for a few seconds —
  // Clarity records that, so recordings show what the user was doing when the
  // connection died (pairs with the ue_afk_timeout / ue_connection_interrupted
  // events for triage).
  const [showDisconnectFrame, setShowDisconnectFrame] = useState(false);
  useEffect(() => {
    if (!disconnectSnapshot) {
      setShowDisconnectFrame(false);
      return undefined;
    }
    setShowDisconnectFrame(true);
    const t = setTimeout(() => setShowDisconnectFrame(false), DISCONNECT_FRAME_MS);
    return () => clearTimeout(t);
  }, [disconnectSnapshot]);

  // ── TEST: auto-disconnect 5s after login (?debugFreezeFrame=1) ──
  // Fires once per page load so you can log in on prod, watch the genuine-drop
  // flow (freeze-frame overlay → Connection Lost modal), then reconnect without
  // being dropped again. No-op unless the flag is present.
  const debugFiredRef = useRef(false);
  useEffect(() => {
    if (!debugFreezeFrameMode || !hasLoggedInOnce || debugFiredRef.current) return undefined;
    debugFiredRef.current = true;
    const t = setTimeout(() => {
      console.log("[RenderNow] debugFreezeFrame: forcing disconnect 5s after login");
      debugForceDisconnect();
    }, 5000);
    return () => clearTimeout(t);
  }, [debugFreezeFrameMode, hasLoggedInOnce, debugForceDisconnect]);

  // ── Console diagnosis when Connection Interrupted modal fires ──
  useEffect(() => {
    if (!refreshRequiredModalVisible) return;

    const events = disconnectEventsRef.current;
    const statusHistory = statusHistoryRef.current;
    const lastTransition = statusHistory[statusHistory.length - 1] ?? "none";
    const status = getStatus();

    const cause = getDisconnectCause(events, status);

    // Same diagnosis already shown in the console — also pushed to Clarity so
    // sessions can be filtered/searched by disconnect cause in the dashboard,
    // since the recording itself is black for the whole streaming segment.
    // AFK timeouts get their own event so "interrupted" sessions in Clarity
    // mean genuine unexpected disconnects, not users who walked away.
    claritySet("ue_disconnect_cause", cause);
    clarityEvent(refreshModalReason === "afk" ? "ue_afk_timeout" : "ue_connection_interrupted");

    const recentEvents = events.slice(-20);

    console.group("%c[RenderNow] Connection Interrupted — Diagnosis", "color:#e53935;font-weight:bold;font-size:13px");
    console.log("%cLikely cause:", "font-weight:bold", cause);
    console.log("%cTrigger transition:", "font-weight:bold", lastTransition);
    console.log("%cstream status:", "font-weight:bold", status ?? "unknown");

    if (recentEvents.length) {
      console.group(`%cRecent stream events (last ${recentEvents.length})`, "font-weight:bold");
      recentEvents.forEach(({ time, type, detail }) => {
        const t = time.slice(11, 23);
        if (detail && Object.keys(detail).length > 0) {
          console.log(`%c${t}  ${type}`, "font-family:monospace", detail);
        } else {
          console.log(`%c${t}  ${type}`, "font-family:monospace");
        }
      });
      console.groupEnd();
    } else {
      console.log("%cNo stream events captured before disconnect.", "color:#888");
    }

    if (statusHistory.length) {
      console.group("%cStatus transition history", "font-weight:bold");
      statusHistory.forEach((s) => console.log(`%c${s}`, "font-family:monospace"));
      console.groupEnd();
    }

    console.groupEnd();
  }, [refreshRequiredModalVisible, refreshModalReason, disconnectEventsRef, statusHistoryRef, getStatus]);

  // ── Video download: auto-trigger when S3 URL arrives ────────
  useEffect(() => {
    if (!videofileone) return;
    const url = videofileone;
    console.log("Video URL:", url);
    setVideofileone("");
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = "Video.mp4";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => console.error("Video download failed:", err));
  }, [videofileone, setVideofileone]);

  // ── Still render image download: auto-trigger when URL arrives ─
  useEffect(() => {
    if (!renderDownloadUrl) return;
    const url = renderDownloadUrl;
    console.log("Render still image URL:", url);
    setRenderDownloadUrl("");
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = "Rendering.JPG";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => console.error("Render image download failed:", err));
  }, [renderDownloadUrl, setRenderDownloadUrl]);

  // ── Scene export download: auto-trigger when S3 URL arrives ───
  // Same fetch->blob->anchor dance as the video/render downloads, but the file name
  // has to come from the URL: an export is a .obj, a .gltf/.glb or a .zip depending
  // on the format picked in UE, and a hardcoded extension would corrupt the rest.
  useEffect(() => {
    if (!exportDownloadUrl) return;
    const url = exportDownloadUrl;
    console.log("Scene export URL:", url);
    setExportDownloadUrl("");
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = exportFileNameFromUrl(url);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => console.error("Scene export download failed:", err));
  }, [exportDownloadUrl, setExportDownloadUrl]);

  // ── Image download helper ─────────────────────────────────────
  const saveImage = useCallback(async () => {
    if (!imagefileone) return;
    const response = await fetch(imagefileone);
    const blob = await response.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = "Image.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(href);
    setPreparingDownload(false);
    setDownloadVisible(false);
  }, [imagefileone, setPreparingDownload, setDownloadVisible]);

  // ── Render ───────────────────────────────────────────────────
  return (
    <div
      className={claritySnapshotsMode ? "arcware-clarity-snapshots" : undefined}
      style={{ position: "relative", backgroundColor: "black" }}
    >
      {/* Behind-the-video Clarity snapshot: a downscaled frame that updates every
          few seconds, layered BEHIND the live stream (see .arcware-clarity-bg).
          The opaque video covers it, so the user never sees it — but Clarity's
          replay (blank <video>) shows it, giving a time-lapse of the session. */}
      {claritySnapshotsMode && liveSnapshot && (
        <img className="arcware-clarity-bg" src={liveSnapshot} alt="" />
      )}

      {/* Arcware stream — the SDK appends its <video> into this container. */}
      <div className="arcware-stream-container" ref={containerRef} />

      {/* Freeze-frame: last video frame, shown briefly after a disconnect.
          Hidden the moment the stream is live again so it never covers video. */}
      {showDisconnectFrame && disconnectSnapshot && psStatus !== "streaming" && (
        <img className="arcware-disconnect-frame" src={disconnectSnapshot} alt="" />
      )}

      {/* Stream state overlay (idle / pending / ready / error). Hidden while streaming. */}
      {psStatus !== "streaming" && (
        <div className="arcware-overlay">
          {/* Idle: stream not yet started. This is the ONLY place our own invented
              status copy belongs — everything from here on is Arcware's to narrate.
              Three states, in order: still initialising, gave up, ready to play. */}
          {IDLE_STATUSES.has(psStatus) && (
            <div className="state state-idle">
              {startupBlocked ? (
                <>
                  <p className="il-idle-text">{startupFailure.headline}</p>
                  {/* The step that actually failed. On screen rather than console-only
                      so a support screenshot places the fault on its own. */}
                  <p className="il-pending-subtext">{startupFailure.detail}</p>
                  <button
                    className="il-state-btn"
                    onClick={() => {
                      if (startupFailure.recoverable) retryCredentials();
                      else window.location.reload();
                    }}
                  >
                    {startupFailure.recoverable ? "Try Again" : "Reload Page"}
                  </button>
                </>
              ) : canStartSession ? (
                <>
                  <p className="il-idle-text">Click play to login</p>
                  <button
                    className="il-play-btn"
                    onClick={() => {
                      connectionStartRef.current = Date.now();
                      play();
                      setShowLoadingTutorial(true);
                    }}
                  >
                    ▶
                  </button>
                </>
              ) : (
                // No Play control at all until credentials + SDK + video host are up.
                // The button is not rendered rather than disabled: a visible control
                // still reads as something to click, and a click that does nothing is
                // indistinguishable from the stall this whole change is about.
                <p className="il-idle-text">
                  Starting your session
                  <span className="il-loading-dots" aria-hidden="true">
                    <i>.</i>
                    <i>.</i>
                    <i>.</i>
                  </span>
                </p>
              )}
            </div>
          )}

          {/* Pending: connecting / queued */}
          {PENDING_OVERLAY_STATUSES.has(psStatus) && !noWorkerAvailable && (
            <div className="state state-pending">
              <div className="il-spinner" />
              {/* A reconnect keeps its own headline. It happens long after Play, in a
                  session that has already streamed, so it sits outside the start flow —
                  and without it the overlay can't say the session is being RECOVERED
                  rather than started, which is the one thing the user needs to know
                  here. Arcware's message keeps its own line beneath, as it always did. */}
              {hasEverStreamed && <p className="il-pending-text">Reconnecting...</p>}
              {/* Arcware's own words, verbatim. The invented ladder that used to live
                  here ("Preparing your session…" → "Servers are spinning up — usually
                  ready within 2–3 minutes." → "Still connecting…") was guesswork keyed
                  off elapsed time: it overrode the real status and could contradict it
                  outright. Nothing is shown until the first message lands — a spinner
                  alone is honest, invented text is not. */}
              {pendingStatusMessage ? (
                <p className={hasEverStreamed ? "il-arcware-status" : "il-pending-text"}>
                  {pendingStatusMessage}
                </p>
              ) : (
                // Arcware has sent nothing YET — this is the gap between connect() and
                // its first signalling message, which showed as an empty line. Filling a
                // gap is not overriding: the instant a real message exists it takes this
                // slot, and this can never replace one.
                !hasEverStreamed && (
                  <p className="il-pending-text">Starting your session...</p>
                )
              )}
              <button className="il-cancel-btn" onClick={() => {
                cancel();
                if (hasEverStreamed) {
                  setRefreshModalReason("stream");
                  setRefreshRequiredModalVisible(true);
                  setShowLoadingTutorial(false);
                }
              }}>
                Cancel
              </button>
            </div>
          )}

          {/* Ready: paused, can resume */}
          {READY_STATUSES.has(psStatus) && (
            <div className="state state-ready">
              <p className="il-pending-text" style={{ textAlign: "center", maxWidth: 320 }}>
                Stream paused. Resume to continue.
              </p>
              <button className="il-state-btn" onClick={() => play()}>
                Resume
              </button>
            </div>
          )}

          {/* Error / terminal session (failed, ended) */}
          {TERMINAL_INTERRUPT_STATUSES.has(psStatus) && (
            <div className="state state-error">
              <p className="il-error-text">
                Connection ended or could not be recovered. Try resuming, or refresh the page if the problem continues.
              </p>
              <button className="il-state-btn" onClick={() => play()}>
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hamburger menu + slide-in panel */}
      <MenuPanel
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        adminSectionOpen={adminSectionOpen}
        setAdminSectionOpen={setAdminSectionOpen}
        playfabData={playfabData}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        viewerMode={viewerMode}
        canShare={isAdmin && !viewerMode}
        onShare={handleShare}
        isLoggedIn={hasLoggedInOnce}
        onLogout={handleLogout}
      />

      {/* Signup intake form — shown on top of loading screen when arriving via ?action=signup */}
      {signupIntakeVisible && (
        <SignupIntakeModal
          onSubmit={() => {
            setSignupIntakeVisible(false);
            setSignupFormSubmitted(true);
          }}
        />
      )}

      {/* User login overlay — never shown in viewer mode (UE logs in as the owner). */}
      {loginVisible && !viewerMode && (
        <LoginPage
          sendMessage={sendMessage}
          error={error}
          setError={setError}
          loginLoading={loginLoading}
          setLoginLoading={setLoginLoading}
          success={success}
          setSuccess={setSuccess}
          resetPasswordLoading={resetPasswordLoading}
          setResetPasswordLoading={setResetPasswordLoading}
          newAccountLoading={newAccountLoading}
          setNewAccountLoading={setNewAccountLoading}
          initialMode={
            !googleLinkEmail && signupMode && !signupNewAccountDone ? "newAccount" : "login"
          }
          initialEmail={googleLinkEmail}
          linkEmail={googleLinkEmail}
          onGoogleLinkLogin={handleGoogleLinkLogin}
          onLinkCancel={handleGoogleLinkCancel}
          onAccountCreatedContinue={() => setSignupNewAccountDone(true)}
          // Direct-login mode: EVERY submit goes through backend validation + the
          // held auto-login — never gate this on canSkipVideo. Gating on it opened a
          // hole: a user who submitted within a few seconds AFTER RenderNow_Loaded
          // went down LoginPage's normal UE path, whose immediate send hit UE's
          // still-initializing login state (empty-credentials PlayFab error), and an
          // unregistered email got a raw UE error instead of the friendly
          // "We couldn't find an account" message. The auto-login itself only waits
          // out whatever remains of the grace window, so late submits aren't delayed.
          // Reset Password stays hidden for the whole ?action=login session — the
          // eventrender.com Login button must never expose it.
          onDirectLogin={directLoginMode ? handleDirectLogin : undefined}
          onDirectSocialLogin={directLoginMode ? handleDirectSocialLogin : undefined}
          onGoogleTrigger={handleGoogleTrigger}
          onLoginTimeout={handleLoginTimeout}
          claritySeeThrough={claritySnapshotsMode}
          onSignupRedirect={directLoginMode ? handleContinueToSignup : undefined}
          hideResetPassword={directLoginMode && !googleLinkEmail}
        />
      )}

      {googleLinkNotice && (
        <div
          role="status"
          style={{
            position: "fixed", left: "50%", bottom: 24, transform: "translateX(-50%)",
            zIndex: 10005, maxWidth: 440, padding: "12px 16px", borderRadius: 8,
            backgroundColor: "#2a2a2a", color: "#fff", fontSize: 14, lineHeight: 1.4,
            textAlign: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          }}
        >
          {googleLinkNotice}
        </div>
      )}

      {/* Admin login overlay */}
      {loginVisibleAdmin && (
        <LoginPageAdmin
          error={errorAdmin}
          setError={setErrorAdmin}
          loginLoading={loginLoadingAdmin}
          setLoginLoading={setLoginLoadingAdmin}
          setLoginVisible={setLoginVisibleAdmin}
          setIsModalOpen={setIsModalOpen}
          claritySeeThrough={claritySnapshotsMode}
        />
      )}

      {/* 3D file upload modal */}
      {upload3DContainerVisible && <Upload3DContainer sendMessage={sendMessage} />}

      {/* Cloudinary Upload Widget (images only — this replaces images in the scene) */}
      <UploadWidget
        isOpen={imgbbContainerVisible}
        options={{ clientAllowedFormats: ["png", "jpg", "jpeg"], resourceType: "image" }}
        onSuccess={(result) => {
          if (result.event === "success" && Boolean(result?.info?.secure_url)) {
            sendMessage(`ImageLinkString,${limitImageResolution(result.info)}`);
            setImgbbContainerVisible(false);
          }
          if (result.info === "hidden") {
            setImgbbContainerVisible(false);
          }
        }}
        onError={(err) => {
          console.log("Image upload error:", err);
          setImgbbContainerVisible(false);
        }}
      />

      {/* Image-to-3D Cloudinary Upload Widget (jpg/jpeg/png only) */}
      <UploadWidget
        isOpen={imageTo3DContainerVisible}
        options={{ clientAllowedFormats: ["jpg", "jpeg", "png"], resourceType: "image" }}
        onSuccess={(result) => {
          if (result.event === "success" && Boolean(result?.info?.secure_url)) {
            sendMessage(`image_to_3d_imguploaded,${limitImageResolution(result.info)}`);
            setImageTo3DContainerVisible(false);
          }
          if (result.info === "hidden") {
            setImageTo3DContainerVisible(false);
          }
        }}
        onError={(err) => {
          console.log("Image to 3D upload error:", err);
          setImageTo3DContainerVisible(false);
        }}
      />

      {/* Download bar */}
      {(renderStarted || (downloadVisible && imagefileone)) && (
        <DownloadBar
          imageUrl={imagefileone}
          onDownloadClick={saveImage}
          onCancel={() => {
            setDownloadVisible(false);
            setPreparingDownload(false);
          }}
          preparingDownload={preparingDownload}
          renderStarted={renderStarted}
        />
      )}

      {/* Veo video link popup */}
      {veoLinkUrl && (
        <VeoLinkPopup url={veoLinkUrl} onClose={() => setVeoLinkUrl("")} />
      )}

      {/* Share viewer link popup (admin) — reuses the copy UI */}
      {shareLinkUrl && (
        <VeoLinkPopup
          url={shareLinkUrl}
          title="Viewer Share Link"
          onClose={() => setShareLinkUrl("")}
        />
      )}
      {shareError && (
        <div className="download-image-popup--container" style={{ gap: 12 }}>
          <span style={{ color: "#e83232", fontWeight: 600, fontSize: 14, flex: 1 }}>
            {shareError}
          </span>
          <button
            onClick={() => setShareError("")}
            style={{
              padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc",
              backgroundColor: "white", color: "#333",
              fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Viewer-mode gate: validating the share token, or showing the invalid-link screen */}
      {shareToken && (viewerValidating || viewerError) && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10011,
          backgroundColor: "rgba(0,0,0,0.9)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          color: "white", fontFamily: "sans-serif", gap: 16, padding: 24, textAlign: "center",
        }}>
          {viewerError ? (
            <>
              <div style={{ fontSize: 22, fontWeight: 600 }}>This share link isn't valid</div>
              <div style={{ fontSize: 15, color: "#a0aec0", maxWidth: 420 }}>
                The link may be incorrect or no longer active. Please ask the person who
                shared it to send you a new link.
              </div>
            </>
          ) : (
            <>
              <div className="il-spinner" />
              <div style={{ fontSize: 15, color: "#cbd5e0" }}>Validating share link…</div>
            </>
          )}
        </div>
      )}

      {noWorkerAvailable && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10001,
          backgroundColor: "rgba(0,0,0,0.85)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          color: "white", fontFamily: "sans-serif", gap: 16, padding: 24,
        }}>
          <div style={{ fontSize: 22, fontWeight: 600 }}>We're sorry! This doesn't happen often</div>
          <div style={{ fontSize: 15, color: "#a0aec0", textAlign: "center", maxWidth: 400 }}>
            Oops, something went wrong with the connection. Please refresh the page and click Play.
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <button
              onClick={() => {
                setNoWorkerAvailable(false);
                connectionStartRef.current = Date.now();
                cancel();
                setTimeout(() => { try { play(); } catch (_) {} }, 300);
              }}
              style={{
                padding: "10px 24px", borderRadius: 8, border: "none",
                backgroundColor: "#111", color: "white",
                fontSize: 15, fontWeight: 600, cursor: "pointer",
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "10px 24px", borderRadius: 8,
                border: "1.5px solid #4a5568", backgroundColor: "transparent", color: "white",
                fontSize: 15, fontWeight: 600, cursor: "pointer",
              }}
            >
              Refresh Page
            </button>
          </div>
          <button
            onClick={() => {
              setReportIssueDiagnostics(
                buildIssueDiagnostics("no_worker", getStatus, disconnectEventsRef, statusHistoryRef)
              );
              setReportIssueOpen(true);
            }}
            style={{
              marginTop: 8, background: "none", border: "none",
              color: "#718096", fontSize: 13, cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Report an issue
          </button>
        </div>
      )}

      {/* The direct-login (?action=login) wait used to be its own opaque overlay, which
          hid the progress bar and the Arcware connection status behind a bare spinner —
          so a stream that never connected looked identical to one that was 10s away, to
          the user and in Clarity recordings alike. It's folded into the loading screen
          instead: same "we're signing you in" message, but the connection status stays
          on screen. Kept mounted while a login is pending so the message can't vanish
          if something else clears showLoadingTutorial first. */}
      {(showLoadingTutorial || autoLoginPending) && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000 }}>
          <LoadingScreen
            canSkipVideo={canSkipVideo}
            handleSkip={handleSkip}
            arcwareMessages={connectionMessages}
            loginPending={autoLoginPending}
            streamStatus={psStatus}
          />
        </div>
      )}

      {/* AFK inactivity countdown — mirrors the SDK overlay we disabled with CoreSetup.
          Any activity (mouse/key/touch, or the button) dismisses it; if it runs out the
          SDK closes the session and the dedicated inactivity modal takes over. */}
      {afkWarning && (
        <div style={{
          // Above the login overlay (10006): the AFK countdown must stay visible even
          // if the user idles with the login page open.
          position: "fixed", inset: 0, zIndex: 10007,
          backgroundColor: "rgba(0,0,0,0.8)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          color: "white", fontFamily: "sans-serif", gap: 16, padding: 24, textAlign: "center",
        }}>
          <div style={{ fontSize: 24, fontWeight: 700 }}>Are you still there?</div>
          <div style={{ fontSize: 15, color: "#cbd5e0", maxWidth: 420, lineHeight: 1.5 }}>
            You've been inactive for a while, so your session is about to close
            {afkWarning.countDown != null && (
              <> in <strong style={{ color: "white" }}>{afkWarning.countDown}s</strong></>
            )}
            . Move your mouse or press any key to keep working.
          </div>
          <button
            onClick={dismissAfkWarning}
            style={{
              padding: "12px 32px", borderRadius: 8, border: "none",
              backgroundColor: "white", color: "#111",
              fontSize: 15, fontWeight: 600, cursor: "pointer",
            }}
          >
            I'm still here
          </button>
        </div>
      )}

      {refreshRequiredModalVisible && (
        <RefreshRequiredModal
          reason={refreshModalReason}
          onReconnect={() => {
            setRefreshRequiredModalVisible(false);
            setRefreshModalReason(null);
            if (refreshModalReason === "loading") {
              startLoadingTimer();
              connectionStartRef.current = Date.now();
            }
            try {
              play();
            } catch (_) {}
          }}
          onReportIssue={() => {
            setReportIssueDiagnostics(
              buildIssueDiagnostics(
                refreshModalReason,
                getStatus,
                disconnectEventsRef,
                statusHistoryRef,
              ),
            );
            setReportIssueOpen(true);
          }}
        />
      )}

      {reportIssueOpen && reportIssueDiagnostics && (
        <ReportIssueModal
          diagnostics={reportIssueDiagnostics}
          onClose={() => {
            setReportIssueOpen(false);
            setReportIssueDiagnostics(null);
          }}
        />
      )}

      <LoadingScreen displayOnly />
    </div>
  );
};

export default InterlucentEmulator;
